/**
 * GameScene: loads a level and owns everything that runs while playing —
 * the Matter engine + world container, camera, ragdoll player, hook, all
 * entities (platforms, hazards, trampolines, boosters, wind, finish), the
 * HUD (level title, pause + fullscreen buttons), the pause menu, win/dead
 * overlays, and the per-level music track.
 *
 * Game loop runs at a fixed 60 Hz timestep with up to MAX_SUBSTEPS catch-up
 * steps per frame so physics stays deterministic if the renderer hitches.
 */

import { Application, Container, Graphics, Text } from 'pixi.js';
import Matter from 'matter-js';
import { Ragdoll } from '../gameplay/Ragdoll.js';
import { Hook } from '../gameplay/Hook.js';
import { Anchor } from '../gameplay/entities/Anchor.js';
import { Platform } from '../gameplay/entities/Platform.js';
import { Hazard } from '../gameplay/entities/Hazard.js';
import { Finish } from '../gameplay/entities/Finish.js';
import { Trampoline } from '../gameplay/entities/Trampoline.js';
import { Booster } from '../gameplay/entities/Booster.js';
import { Wind } from '../gameplay/entities/Wind.js';
import { MovingPlatform } from '../gameplay/entities/MovingPlatform.js';
import { MovingHazard } from '../gameplay/entities/MovingHazard.js';
import { Switch } from '../gameplay/entities/Switch.js';
import { Gate } from '../gameplay/entities/Gate.js';
import { Checkpoint } from '../gameplay/entities/Checkpoint.js';
import { Camera } from '../gameplay/Camera.js';
import { Confetti } from '../gameplay/Confetti.js';
import { Input } from '../app/Input.js';
import { GamepadInput, GamepadButton } from '../app/Gamepad.js';
import { EspButtonClient } from '../app/EspButtonClient.js';
import { Sfx, unlockAudioOnFirstGesture, setSfxMuted, isSfxMuted } from '../audio/Sfx.js';
import { Music, type Song } from '../audio/Music.js';
import { listLevels, type LevelData } from '../gameplay/LevelLoader.js';
import { openJukeboxModal as openJukeboxModalImpl, type JukeboxController } from './game/modals/JukeboxModal.js';
import { openMyLevelsModal as openMyLevelsModalImpl, type MyLevelsController } from './game/modals/MyLevelsModal.js';
import { openLevelSelectModal as openLevelSelectModalImpl, type LevelSelectController } from './game/modals/LevelSelectModal.js';
import { openCustomizeModal as openCustomizeModalImpl, type CustomizeController } from './game/modals/CustomizeModal.js';
import { openLoadReplayModal as openLoadReplayModalImpl, type LoadReplayController } from './game/modals/LoadReplayModal.js';
import { openLevelStatsModal as openLevelStatsModalImpl, type LevelStatsController } from './game/modals/LevelStatsModal.js';
import { openInputModal as openInputModalImpl, type InputModalController } from './game/modals/InputModal.js';
import * as LevelStore from '../editor/LevelStore.js';
import {
  encodeReplay, mulberry32, pickSeed,
  type Replay, type ReplayInput,
} from '../gameplay/Replay.js';
import * as Progress from '../gameplay/Progress.js';
import * as LevelStats from '../gameplay/LevelStats.js';
import { newRunStats, formatStatsLine, type RunStats } from '../gameplay/RunStats.js';
import { resolveGradient, formatFrameTime } from './game/Theme.js';
import {
  makeIconButton as buildIconButton,
  buildReplayButtonRow,
  makeHamburgerButton,
  type ReplayButtonSpec,
} from './game/ReplayHud.js';
import { PauseMenu, type PauseMenuItem } from './game/PauseMenu.js';
import { SafeAreaProbe } from './game/SafeArea.js';
import { Hud, toggleFullscreen as toggleFullscreenImpl } from './game/Hud.js';

/** Host hooks the scene calls when it wants the app shell to swap scenes. */
export interface GameSceneHost {
  onEnterEditor?: (level: LevelData) => void;
  /** Invoked when a test-mode session ends (Return to Editor). */
  onExitTest?: () => void;
}

/** Optional construction-time options. */
export interface GameSceneOptions {
  /** When set, the scene starts in test mode loaded with this exact level. */
  testLevel?: LevelData;
  /** When set, the scene boots straight into a deterministic playback of
   *  the given replay. If `level` is provided it overrides the built-in
   *  catalog (used for shared user-level replays). When omitted, the
   *  replay's `levelId` is resolved against the built-in catalog. */
  replay?: { replay: Replay; level?: LevelData };
  /** When set, the scene plays this song instead of the level's default
   *  music — both for live play and during a replay. Used by share links
   *  that capture a custom track choice (`?t=<name>` or `?T=<json>`). */
  track?: Song;
}

const FIXED_DT_MS = 1000 / 60;
const MAX_SUBSTEPS = 5;


export class GameScene {
  private engine!: Matter.Engine;
  private worldContainer!: Container;
  private overlay!: Container;
  private bgContainer!: Container;
  private gameLayer!: Container;
  private hudLayer!: Container;
  private hud!: Hud;
  private ragdoll!: Ragdoll;
  private hook!: Hook;
  private camera!: Camera;
  private anchors: Anchor[] = [];
  private platforms: Platform[] = [];
  private hazards: Hazard[] = [];
  private trampolines: Trampoline[] = [];
  private boosters: Booster[] = [];
  /** Body → entity lookups so collision handlers don't have to scan arrays. */
  private trampolineByBody: Map<Matter.Body, Trampoline> = new Map();
  private boosterByBody: Map<Matter.Body, Booster> = new Map();
  private winds: Wind[] = [];
  private movingPlatforms: MovingPlatform[] = [];
  private movingHazards: MovingHazard[] = [];
  private switches: Switch[] = [];
  private gates: Gate[] = [];
  /** Body → Switch lookup so the active-overlap pass is O(1) per pair. */
  private switchByBody: Map<Matter.Body, Switch> = new Map();
  private checkpoints: Checkpoint[] = [];
  /** Index of the highest checkpoint the player has touched on this attempt.
   *  Survives `reloadCurrent()` (which rebuilds the scene) so the respawn
   *  override holds across deaths. Reset to -1 on level change. */
  private activeCheckpointIdx = -1;
  /** Seconds elapsed in the current scene; drives all path math. */
  private worldTimeSec = 0;
  private finish!: Finish;
  private input!: Input;
  private accumulatorMs = 0;
  private state: 'play' | 'won' | 'dead' = 'play';
  private bouncedThisStep = false;
  /** Cooldown clock for swoosh SFX so we don't spam the audio bus. */
  private nextSwooshAt = 0;
  /** Tracks whether the torso is currently inside any wind zone (for whoosh-on-enter). */
  private inWind = false;
  private level!: LevelData;
  private levelIndex = 0;
  private allLevels: LevelData[];
  private hudText!: Text;
  private music: Music | null = null;
  private musicMuted = false;
  /** Custom-track override chosen via the jukebox for the current level
   *  only. Cleared whenever the player switches to a different level so
   *  every level starts on its deterministic per-id default again. A retry
   *  / respawn rebuilds the same level index and therefore keeps the pick.
   *  Also seeded once from a share-link `?t=`/`?T=` param so demos play
   *  the recorder's track for the level being demonstrated. */
  private trackOverride: Song | null = null;
  private paused = false;
  private pauseOverlay!: Container;
  /** Current vertical scroll offset of the pause menu (CSS-equivalent units). */
  /** Maximum scroll value (content overflow); 0 when content fits. */
  private pauseMenu!: PauseMenu;
  /** Touch drag tracking for pause-menu scrolling. */
  private pauseDragLastY: number | null = null;
  private pauseDragMovedTotal = 0;
  private myLevelsModal: MyLevelsController | null = null;
  private levelSelectModal: LevelSelectController | null = null;
  private replayModal: LoadReplayController | null = null;
  private jukebox: JukeboxController | null = null;
  private customizeModal: CustomizeController | null = null;
  private levelStatsModal: LevelStatsController | null = null;
  private inputModal: InputModalController | null = null;
  private espClients: EspButtonClient[] = [];
  // (HUD hits live on this.hud.hits)
  /** Buttons drawn on the win/dead overlay (Save Replay etc.). */
  private overlayHits: Array<{ x: number; y: number; w: number; h: number; action: () => void }> = [];
  /** True while the win/dead overlay is up. Decoupled from `state` so the
   *  background replay can keep state='play' (physics running) while the
   *  overlay still routes taps to its buttons. */
  private overlayShowing = false;
  /** Original outcome that produced the overlay; used for tap routing
   *  (advance to next level vs retry) regardless of replay state. */
  private overlayOutcome: 'won' | 'dead' | null = null;
  /** Pixi node that draws the replay timeline progress bar; redrawn each
   *  frame while a background replay is playing. */
  private timelineFill: Graphics | null = null;
  private timelineRect: { x: number; y: number; w: number; h: number } | null = null;
  /** Confetti emitter shown on level clear. Lives on the overlay layer so it
   *  draws above the world but below the win UI text. */
  private confetti: Confetti | null = null;
  /** Sub-container of `overlay` holding the title/subtitle/buttons/timeline,
   *  so we can fade-and-hide it as a unit while the hamburger restore button
   *  stays visible. */
  private winContent: Container | null = null;
  /** Floating hamburger button (Pixi) that brings the win UI back. */
  private hamburgerBtn: Container | null = null;
  /** Hit rect for the hamburger button (kept separate from overlayHits so it
   *  stays clickable while winContent is hidden). */
  private hamburgerHit: { x: number; y: number; w: number; h: number; action: () => void } | null = null;
  /** Timestamp (ms, performance.now) when the win UI should auto-hide. 0 = no auto-hide pending. */
  private winAutoHideAt = 0;
  /** True once the auto-hide timer fired and we're animating the fade. */
  private winContentFading = false;
  /** True once the user has manually dismissed the win UI; suppresses
   *  the next auto-hide so re-opening via hamburger doesn't auto-vanish. */
  private winContentManuallyHidden = false;
  /** Survives showOverlay() resets so the win UI stays hidden across
   *  background-replay scene rebuilds (whether hidden by auto-fade or by
   *  the player tapping the hamburger). */
  private winContentHiddenSticky = false;

  // --- Replay state --------------------------------------------------------
  /** Seeded gameplay RNG; used in physics paths instead of Math.random(). */
  private gameRng: () => number = Math.random;
  /** Seed currently driving gameRng — recorded into the replay header. */
  private currentSeed = 0;
  /** Fixed-step frame counter; advances once per physics substep. */
  private frameCount = 0;
  /** Inputs captured during the live run; consumed when saving the replay. */
  private recordedInputs: ReplayInput[] = [];
  /** When set, the next buildScene plays back this replay deterministically. */
  private pendingReplay: Replay | null = null;
  /** 'playback' = scene is replaying a saved demo; null = live play. */
  private replayMode: 'playback' | null = null;
  /** Cursor into pendingReplay.inputs while playing back. */
  private replayCursor = 0;
  /** Most recent finished run, exposed via the win-screen save buttons. */
  private lastReplay: Replay | null = null;
  /** Per-run "fun stats" — cheap-to-track metrics surfaced on the win/death
   *  overlays. Reset in buildScene(); updated each fixed step in update();
   *  snapshotted into `lastStats` when a run ends so the overlay can read
   *  them after `state` flips. */
  private runStats = newRunStats();
  /** Snapshot of `runStats` at the moment of the run's end (win or death).
   *  Decoupled from the live `runStats` so a respawn-in-place after death
   *  doesn't wipe what the death overlay is displaying. */
  private lastStats: RunStats | null = null;
  /** True when the run that just finished beat the prior best (or first
   *  completion). Drives the "NEW BEST!" subtitle + extra confetti burst on
   *  the win overlay. Cleared when the player advances past the overlay. */
  private justSetNewBest = false;

  /** Tear-down callbacks accumulated during construction (window listeners,
   * ticker handler, etc.) so the app shell can swap scenes cleanly. */
  private readonly disposers: Array<() => void> = [];
  /** Marks that destroy() has been called so async callbacks can no-op. */
  private destroyed = false;
  /** True when a coalesced resize is queued for the next animation frame. */
  private resizePending = false;
  /** Off-screen probe element used to read env(safe-area-inset-*) in CSS px. */
  private safeArea = new SafeAreaProbe();
  /** When true, the scene was launched from the editor with a one-off level. */
  private testMode = false;
  /** Stable bound ticker callback so we can remove it on destroy. */
  private boundUpdate?: (ticker: { deltaMS: number }) => void;
  private wheelHandler: ((e: WheelEvent) => void) | null = null;
  private gamepadInput!: GamepadInput;

  // Pre-bound listeners reused across level rebuilds so reference equality holds
  // for any future Matter.Events.off() and we avoid per-build closure allocs.
  private readonly boundOnCollision = (evt: Matter.IEventCollision<Matter.Engine>) => this.onCollision(evt);
  private readonly boundOnCollisionActive = (evt: Matter.IEventCollision<Matter.Engine>) => this.onCollisionActive(evt);

  constructor(
    private readonly app: Application,
    private readonly host: GameSceneHost = {},
    options: GameSceneOptions = {},
  ) {
    this.allLevels = listLevels();
    if (this.allLevels.length === 0) throw new Error('No levels found');
    // Restore persisted audio settings.
    try {
      this.musicMuted = localStorage.getItem('shk.musicMuted') === '1';
      setSfxMuted(localStorage.getItem('shk.sfxMuted') === '1');
    } catch { /* ignore storage errors */ }
    unlockAudioOnFirstGesture();
    if (options.testLevel) {
      this.testMode = true;
      this.loadLevelByData(options.testLevel);
    } else if (options.replay) {
      // Booted from a share link / external replay. Set the pending
      // replay first so buildScene() picks it up, then load the level
      // either inline (user level) or from the built-in catalog.
      this.pendingReplay = options.replay.replay;
      // Track override travels alongside the replay so playback uses the
      // recorder's chosen song, not whatever the receiver had picked.
      if (options.track) this.trackOverride = options.track;
      const inline = options.replay.level;
      if (inline) {
        this.testMode = false;
        this.loadLevelByData(inline);
      } else {
        const idx = this.allLevels.findIndex((l) => l.id === options.replay!.replay.levelId);
        this.loadLevelByIndex(idx >= 0 ? idx : 0);
      }
    } else {
      this.loadLevelByIndex(Progress.getResumeIndex(this.allLevels.map((l) => l.id)));
    }

    this.gamepadInput = new GamepadInput({
      onButtonDown: (btn) => {
        if (btn === GamepadButton.START) {
          this.togglePause();
          return;
        }
        if (this.paused) {
          const padDir: { [k: number]: 'up' | 'down' | 'left' | 'right' } = {
            [GamepadButton.DPAD_UP]: 'up',
            [GamepadButton.DPAD_DOWN]: 'down',
            [GamepadButton.DPAD_LEFT]: 'left',
            [GamepadButton.DPAD_RIGHT]: 'right',
          };
          if (btn in padDir) { this.pauseMenu.moveFocus(padDir[btn]); return; }
          if (btn === GamepadButton.A) { this.pauseMenu.activateFocused(); return; }
          return;
        }
        if (btn === GamepadButton.BACK) {
          Sfx.uiClick();
          if (this.replayMode === 'playback') this.exitReplayAndPlay();
          else this.reloadCurrent();
          return;
        }
        // A / any face button or trigger = grapple (hold to attach)
        if (btn === GamepadButton.A || btn === GamepadButton.B ||
            btn === GamepadButton.X || btn === GamepadButton.Y ||
            btn === GamepadButton.LB || btn === GamepadButton.RB ||
            btn === GamepadButton.LT || btn === GamepadButton.RT) {
          if (this.state !== 'play') { this.handleOverlayTap(); return; }
          if (this.replayMode === 'playback') return;
          const p = this.ragdoll.position;
          this.recordedInputs.push({ frame: this.frameCount, kind: 'D', x: p.x, y: p.y });
          this.hook.tryAttach(this.anchors);
        }
      },
      onButtonUp: (btn) => {
        if (this.paused) return;
        if (btn === GamepadButton.A || btn === GamepadButton.B ||
            btn === GamepadButton.X || btn === GamepadButton.Y ||
            btn === GamepadButton.LB || btn === GamepadButton.RB ||
            btn === GamepadButton.LT || btn === GamepadButton.RT) {
          if (this.replayMode === 'playback') return;
          if (this.state !== 'play') return;
          this.recordedInputs.push({ frame: this.frameCount, kind: 'U' });
          this.hook.detach();
        }
      },
    });
    this.disposers.push(() => this.gamepadInput.destroy());

    // ESP32 hardware buttons — zero overhead when no URLs are configured.
    this.espClients = this.buildEspClients(this.readEspButtonUrls());
    this.disposers.push(() => { for (const c of this.espClients) c.close(); });

    this.input = new Input(this.app.canvas as HTMLCanvasElement, {
      onDown: (x, y) => {
        // Pause overlay swallows all input.
        if (this.paused) {
          // Start tracking a drag-to-scroll if the press is inside the
          // viewport. We still attempt the hit-test on pointer-up so a tap
          // selects the button, but only if the user didn't actually drag.
          this.pauseDragLastY = y;
          this.pauseDragMovedTotal = 0;
          const vp = this.pauseMenu.scrollViewport;
          if (!vp || x < vp.x || x > vp.x + vp.w || y < vp.y || y > vp.y + vp.h) {
            // Outside the scroll viewport: treat as a button click immediately.
            this.hitTest(this.pauseMenu.hits, x, y);
            this.pauseDragLastY = null;
          }
          return;
        }
        // HUD pause button is always tappable, even on win/dead overlays.
        if (this.hitTest(this.hud.hits, x, y)) return;
        // Hamburger restore button: top-priority on the win overlay so it
        // can re-show the UI even when winContent is hidden.
        if (this.hamburgerHit) {
          const r = this.hamburgerHit;
          if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) { r.action(); return; }
        }
        // Overlay (win/dead) is the next-priority hit layer; checked
        // independently of `state` so the background replay (which keeps
        // state='play') still routes its button clicks correctly.
        if (this.overlayShowing) {
          // Don't route taps to buttons whose content has faded away —
          // they're hidden behind the hamburger restore flow.
          const contentVisible = !this.winContent || (this.winContent.visible && this.winContent.alpha > 0.1);
          if (contentVisible && this.hitTest(this.overlayHits, x, y)) return;
          this.handleOverlayTap();
          return;
        }
        if (this.state !== 'play') {
          this.handleOverlayTap();
          return;
        }
        // Replays drive the hook themselves; ignore live input during playback.
        if (this.replayMode === 'playback') return;
        // Frame is the upcoming physics substep, so playback dispatches at the
        // matching boundary and reproduces the same hook attachment.
        this.recordedInputs.push({ frame: this.frameCount, kind: 'D', x, y });
        this.hook.tryAttach(this.anchors);
      },
      onUp: (x, y) => {
        if (this.paused) {
          // Pause-menu drag: only treat as a tap if the user barely moved.
          if (this.pauseDragLastY !== null && this.pauseDragMovedTotal < 8) {
            // Translate pointer y into pre-scroll content space so the hit
            // rects (stored at item-relative positions) match.
            this.hitTest(this.pauseMenu.hits, x, y + this.pauseMenu.scroll);
          }
          this.pauseDragLastY = null;
          this.pauseDragMovedTotal = 0;
          return;
        }
        if (this.replayMode === 'playback') return;
        if (this.state !== 'play') return;
        this.recordedInputs.push({ frame: this.frameCount, kind: 'U' });
        this.hook.detach();
      },
      onMove: (_x, y) => {
        if (!this.paused) return;
        if (this.pauseDragLastY === null) return;
        const dy = y - this.pauseDragLastY;
        this.pauseDragLastY = y;
        this.pauseDragMovedTotal += Math.abs(dy);
        // Drag down → scroll content up (i.e. reveal items above).
        this.pauseMenu.scrollBy(-dy);
      },
    });

    const boundUpdate = (ticker: { deltaMS: number }) => this.update(ticker.deltaMS);
    this.boundUpdate = boundUpdate;
    this.app.ticker.add(boundUpdate);

    // Mouse-wheel scrolling for the pause menu (desktop). Touch devices use
    // pointer drag handled in the Input callbacks above.
    this.wheelHandler = (e: WheelEvent) => {
      if (!this.paused) return;
      if (this.pauseMenu.scrollMax <= 0) return;
      e.preventDefault();
      this.pauseMenu.scrollBy(e.deltaY);
    };
    (this.app.canvas as HTMLCanvasElement).addEventListener('wheel', this.wheelHandler, { passive: false });

    const onKey = (e: KeyboardEvent) => {
      // Don't steal letter keys from text inputs (e.g. naming a custom
      // track in the save-replay / jukebox modals). Without this, keys
      // like 'r','n','e','p','f' get swallowed by the global shortcuts
      // and never reach the focused field.
      const t = e.target as HTMLElement | null;
      if (t) {
        const tag = t.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (t as HTMLElement).isContentEditable) return;
      }
      if (e.key === 'Escape' || e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        this.togglePause();
        return;
      }
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        Sfx.uiClick();
        this.toggleFullscreen();
        return;
      }
      if (this.paused) {
        const arrowDir: Partial<Record<string, 'up' | 'down' | 'left' | 'right'>> = {
          ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
        };
        const dir = arrowDir[e.key];
        if (dir) { e.preventDefault(); this.pauseMenu.moveFocus(dir); return; }
        if (e.key === 'Enter') { e.preventDefault(); this.pauseMenu.activateFocused(); return; }
        return;
      }
      if (e.key === 'r' || e.key === 'R') {
        Sfx.uiClick();
        // R during replay playback should hand control back to the player
        // (matches pause-menu Restart's behavior); otherwise it would just
        // restart the recorded playback.
        if (this.replayMode === 'playback') this.exitReplayAndPlay();
        else this.reloadCurrent();
      }
      if (e.key === 'n' || e.key === 'N') {
        Sfx.uiClick();
        // Same rationale as R: leaving the watched level via N should end
        // the replay rather than carry the queue forward.
        if (this.replayMode === 'playback') { this.pendingReplay = null; this.replayMode = null; }
        this.nextLevel();
      }
      if ((e.key === 'e' || e.key === 'E') && this.host.onEnterEditor) {
        Sfx.uiClick();
        this.host.onEnterEditor(this.level);
      }
      // Space = simulate the "tap" gesture: hold to grapple, release to let
      // go. Lets keyboard players grapple without a pointer. tryAttach()
      // already auto-picks the nearest in-range anchor so we don't need a
      // cursor target — pass the ragdoll's torso position purely so the
      // recorded replay event has plausible coordinates for any future
      // tooling that inspects them. e.repeat suppresses key-repeat fire.
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        if (e.repeat) return;
        if (this.state !== 'play') { this.handleOverlayTap(); return; }
        if (this.replayMode === 'playback') return;
        const p = this.ragdoll.position;
        this.recordedInputs.push({ frame: this.frameCount, kind: 'D', x: p.x, y: p.y });
        this.hook.tryAttach(this.anchors);
      }
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      if (e.key === ' ' || e.code === 'Space') {
        const t = e.target as HTMLElement | null;
        if (t) {
          const tag = t.tagName;
          if (tag === 'INPUT' || tag === 'TEXTAREA' || (t as HTMLElement).isContentEditable) return;
        }
        if (this.paused) return;
        if (this.replayMode === 'playback') return;
        if (this.state !== 'play') return;
        e.preventDefault();
        this.recordedInputs.push({ frame: this.frameCount, kind: 'U' });
        this.hook.detach();
      }
    };
    const onResize = () => this.scheduleResize();
    const onFsChange = () => this.scheduleResize();
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    document.addEventListener('fullscreenchange', onFsChange);
    // iOS Safari: visualViewport.resize fires AFTER the canvas has actually
    // settled into its new size (the plain `resize` event can fire mid-
    // orientation with stale dims, leaving the camera/HUD offset).
    const vv = (typeof window !== 'undefined' ? window.visualViewport : null);
    if (vv) vv.addEventListener('resize', onResize);
    this.disposers.push(
      () => window.removeEventListener('keydown', onKey),
      () => window.removeEventListener('keyup', onKeyUp),
      () => window.removeEventListener('resize', onResize),
      () => window.removeEventListener('orientationchange', onResize),
      () => document.removeEventListener('fullscreenchange', onFsChange),
      () => { if (vv) vv.removeEventListener('resize', onResize); },
    );
  }

  /** Tear down so the app shell can swap to a different scene. */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const d of this.disposers) {
      try { d(); } catch { /* keep going */ }
    }
    this.disposers.length = 0;
    if (this.boundUpdate) { this.app.ticker.remove(this.boundUpdate as never); delete this.boundUpdate; }
    if (this.music) { this.music.stop(); this.music = null; }
    if (this.engine) {
      Matter.Events.off(this.engine, 'collisionStart', this.boundOnCollision);
      Matter.Events.off(this.engine, 'collisionActive', this.boundOnCollisionActive);
      Matter.World.clear(this.engine.world, false);
      Matter.Engine.clear(this.engine);
    }
    this.app.stage.removeChildren();
    if (this.myLevelsModal) { this.myLevelsModal.close(); this.myLevelsModal = null; }
    if (this.levelSelectModal) { this.levelSelectModal.close(); this.levelSelectModal = null; }
    if (this.replayModal) { this.replayModal.close(); this.replayModal = null; }
    if (this.jukebox) { this.closeJukeboxModal(); }
    if (this.levelStatsModal) { this.levelStatsModal.close(); this.levelStatsModal = null; }
    if (this.input && typeof (this.input as unknown as { destroy?: () => void }).destroy === 'function') {
      (this.input as unknown as { destroy: () => void }).destroy();
    }
    if (this.wheelHandler) {
      try { (this.app.canvas as HTMLCanvasElement).removeEventListener('wheel', this.wheelHandler); } catch { /* ignore */ }
      this.wheelHandler = null;
    }
    this.safeArea.destroy();
  }

  /** Open a DOM modal listing the player's saved user levels. Picking one
   *  loads it via loadLevelByData so it plays exactly like a builtin. */
  private openMyLevelsModal(): void {
    if (this.myLevelsModal) { this.myLevelsModal.close(); this.myLevelsModal = null; }
    this.myLevelsModal = openMyLevelsModalImpl({
      onPick: (entry) => {
        this.pendingReplay = null;
        try { this.loadLevelByData(entry.data); }
        catch (err) { console.warn('Failed to load user level', err); }
      },
      onClose: () => { this.myLevelsModal = null; },
    });
  }


  // -------------------------------------------------------------------------
  // Jukebox
  // -------------------------------------------------------------------------

  /** Tear down the jukebox preview music + visualizer rAF loop. Idempotent. */
  private closeJukeboxModal(): void {
    if (this.jukebox) { this.jukebox.close(); this.jukebox = null; }
  }

  /** Open the jukebox modal. Mutes the level music while the modal is up
   *  and restores the previous mute state on close. */
  private openJukeboxModal(): void {
    this.closeJukeboxModal();
    if (this.music) this.music.setMuted(true);
    this.jukebox = openJukeboxModalImpl({
      onClose: (picked) => {
        this.jukebox = null;
        // If the user left the jukebox with a song actively playing,
        // adopt it as the level's music. Otherwise restore the previous
        // (possibly default-pick) track at the user's mute setting.
        if (picked) {
          this.trackOverride = picked;
          if (this.music) this.music.stop();
          this.music = new Music(picked);
          this.music.setMuted(this.musicMuted);
          this.music.start();
        } else if (this.music) {
          this.music.setMuted(this.musicMuted);
        }
        // If the user opened the jukebox from the pause menu, the game
        // is still paused under the modal — redraw the pause UI so they
        // can resume / pick another action.
        if (this.paused) this.showPauseMenu();
      },
    });
  }

  /** Open the customize modal: skin presets + cosmetic slot pickers. Each
   *  selection auto-saves and re-applies to the live ragdoll instantly so
   *  the player can preview their look against the actual scene. */
  private openCustomizeModal(): void {
    if (this.customizeModal) { this.customizeModal.close(); this.customizeModal = null; }
    this.customizeModal = openCustomizeModalImpl({
      onApply: (skin) => { this.ragdoll.applySkin(skin); },
      onClose: () => {
        this.customizeModal = null;
        if (this.paused) this.showPauseMenu();
      },
    });
  }

  /** Input Settings modal: configure ESP32 WebSocket button URLs, view
   *  connected gamepads, and see keyboard shortcuts. Leaves the game paused
   *  underneath and re-shows the pause UI on close. */
  private openInputModal(): void {
    if (this.inputModal) { this.inputModal.close(); this.inputModal = null; }
    this.inputModal = openInputModalImpl({
      onUrlsChanged: (urls) => {
        // Reconnect the live ESP button pool with the updated URL list.
        this.reconnectEspClients(urls);
      },
      onClose: () => {
        this.inputModal = null;
        if (this.paused) this.showPauseMenu();
      },
    });
  }

  /** "Level Stats" modal: per-level telemetry table + copy-to-clipboard
   *  buttons. Like the customize/jukebox modals it leaves the game paused
   *  underneath and re-shows the pause UI on close. */
  private openLevelStatsModal(): void {
    if (this.levelStatsModal) { this.levelStatsModal.close(); this.levelStatsModal = null; }
    this.levelStatsModal = openLevelStatsModalImpl({
      allLevels: this.allLevels,
      onClose: () => {
        this.levelStatsModal = null;
        if (this.paused) this.showPauseMenu();
      },
    });
  }


  private hitTest(rects: Array<{ x: number; y: number; w: number; h: number; action: () => void }>, x: number, y: number): boolean {    for (const r of rects) {
      if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
        r.action();
        return true;
      }
    }
    return false;
  }

  // --------------------------------------------------------------------------

  private loadLevelByIndex(idx: number): void {
    const newIdx = ((idx % this.allLevels.length) + this.allLevels.length) % this.allLevels.length;
    // Outgoing-attempt accounting: any nav (including a Restart on the
    // same level) ends the prior attempt. If we were mid-run, flush it
    // to LevelStats. Replay playback and editor test-mode never
    // contribute to live stats.
    this.flushAbandonIfPlaying();
    if (newIdx !== this.levelIndex) {
      // True level switch: drop any checkpoint progress from the previous one.
      this.activeCheckpointIdx = -1;
      // Jukebox picks are scoped per-level: changing levels reverts to the
      // new level's deterministic default track.
      this.trackOverride = null;
    }
    this.levelIndex = newIdx;
    this.level = this.allLevels[this.levelIndex]!;
    Progress.recordLastPlayed(this.level.id);
    // Count this as a fresh play attempt for the level. Skip during replay
    // playback and editor test-mode so the stats reflect real player runs.
    if (!this.testMode && !this.pendingReplay) {
      LevelStats.recordPlay(this.level.id);
    }
    this.buildScene();
  }

  /** Load an arbitrary LevelData (e.g. an editor draft) without touching the
   *  builtin-levels list. Used by test mode. */
  private loadLevelByData(level: LevelData): void {
    this.flushAbandonIfPlaying();
    this.levelIndex = -1;
    this.activeCheckpointIdx = -1;
    this.trackOverride = null;
    this.level = level;
    this.buildScene();
  }

  /** Pending kind for the next `flushAbandonIfPlaying` call. Set by
   *  `nextLevel()` so navigating forward via Next is recorded as a "skip"
   *  rather than a generic "abandon", and by `reloadCurrent()` so the
   *  Restart button counts as its own "restart" bucket. Cleared after
   *  the flush. */
  private pendingAbandonKind: 'abandon' | 'skip' | 'restart' = 'abandon';

  /** Close the books on the previous attempt before navigating to a new
   *  one. Three end-of-attempt cases are handled here:
   *    - state==='play': player walked away mid-run → record an abandon
   *      (or skip if `nextLevel()` flagged the kind).
   *    - state==='dead': player died and chose to nav away from the
   *      death overlay → no abandon counter (the death is already
   *      counted), but we still need to feed `frameCount` into the
   *      total-time / longest-attempt aggregates.
   *    - state==='won': totals were already recorded by `recordWin` in
   *      win(); skip to avoid double-counting.
   *  Replay playback and editor test-mode never contribute to live stats. */
  private flushAbandonIfPlaying(): void {
    const kind = this.pendingAbandonKind;
    this.pendingAbandonKind = 'abandon';
    if (this.testMode) return;
    if (this.replayMode === 'playback') return;
    if (this.levelIndex < 0) return;
    if (this.state === 'won') return;
    // Skip the boot-time pseudo-attempt (no frames have ticked yet).
    if (this.frameCount <= 0) return;
    const prev = this.allLevels[this.levelIndex];
    if (!prev) return;
    if (this.state === 'play') {
      LevelStats.recordAbandon(prev.id, this.frameCount, kind);
    } else {
      // state === 'dead': fold the attempt's elapsed frames into the
      // totals without bumping abandons/skips.
      LevelStats.recordAttemptTime(prev.id, this.frameCount);
    }
  }

  private buildScene(): void {
    if (this.worldContainer) this.worldContainer.destroy({ children: true });
    if (this.overlay) this.overlay.destroy({ children: true });
    if (this.hudLayer) this.hudLayer.destroy({ children: true });
    this.app.stage.removeChildren();

    // Swap music for the new level. Music.start() is a no-op until the user
    // gesture unlock; AudioContext stays at 'suspended' until then anyway.
    // If the player picked a custom track via the jukebox for THIS level
    // (or a share link carried one), use it; the override is cleared on
    // every true level switch in loadLevelByIndex so the new level always
    // starts on its own default song.
    if (this.music) this.music.stop();
    this.music = this.trackOverride ? new Music(this.trackOverride) : new Music(this.level.id);
    this.music.setMuted(this.musicMuted);
    this.music.start();

    // ----- Determinism setup MUST happen before any Matter body is built.
    // Body construction calls Common.nextId() (and may call Common.random()
    // for jitter), so seeding the shim *after* construction leaves the
    // bodies tagged with IDs from the previous run. Contact-pair lookup
    // keys on (idA, idB), and the solver breaks ties using random(), so a
    // stale id ordering produces a different simulation trajectory —
    // the exact "replays work half the time" symptom.
    //
    // Resolve the seed first (pendingReplay forces playback).
    if (this.pendingReplay && this.pendingReplay.levelId === this.level.id) {
      this.replayMode = 'playback';
      this.replayCursor = 0;
      this.currentSeed = this.pendingReplay.seed;
    } else {
      // Drop any dangling replay queued for a *different* level. Without
      // this, navigating away (e.g. Pause → Levels → pick a different
      // level) and later returning to the original level would silently
      // re-enter playback because `pendingReplay` was never cleared. The
      // auto-loop path (`scheduleReplayRebuild`) bypasses this branch
      // entirely (it goes straight to `buildScene` with a matching id),
      // so loop-while-watching keeps working.
      this.pendingReplay = null;
      this.replayMode = null;
      this.replayCursor = 0;
      this.currentSeed = pickSeed();
    }
    this.gameRng = mulberry32(this.currentSeed);
    const rng = this.gameRng;
    (Matter.Common as { random: (min?: number, max?: number) => number }).random =
      (min?: number, max?: number) => {
        const lo = typeof min === 'number' ? min : 0;
        const hi = typeof max === 'number' ? max : 1;
        return lo + rng() * (hi - lo);
      };
    // Reset Matter's body-ID counter BEFORE creating any bodies so each
    // run's bodies get IDs 1..N starting from a clean slate.
    (Matter.Common as unknown as { _nextId: number })._nextId = 0;

    this.engine = Matter.Engine.create({ gravity: { x: 0, y: 0.85, scale: 0.001 } });

    this.worldContainer = new Container();
    this.app.stage.addChild(this.worldContainer);

    this.bgContainer = new Container();
    this.gameLayer = new Container();
    // None of the in-world layers receive pointer events; mark them so Pixi's
    // hit-test traversal short-circuits each frame.
    this.bgContainer.eventMode = 'none';
    this.gameLayer.eventMode = 'none';
    this.worldContainer.addChild(this.bgContainer);
    this.worldContainer.addChild(this.gameLayer);

    this.hudLayer = new Container();
    this.app.stage.addChild(this.hudLayer);
    this.hud = new Hud(this.hudLayer);

    this.drawBackground();

    this.anchors = this.level.anchors.map((a) => {
      const node = new Anchor(a);
      // Path breadcrumb (only present for moving anchors) renders below the knob.
      this.gameLayer.addChild(node.pathView);
      this.gameLayer.addChild(node.view);
      return node;
    });

    this.platforms = this.level.platforms.map((p) => {
      const node = new Platform(p);
      this.gameLayer.addChild(node.view);
      Matter.Composite.add(this.engine.world, node.body);
      return node;
    });

    this.hazards = this.level.hazards.map((h) => {
      const node = new Hazard(h);
      this.gameLayer.addChild(node.view);
      Matter.Composite.add(this.engine.world, node.body);
      return node;
    });

    this.trampolines = (this.level.trampolines ?? []).map((t) => {
      const node = new Trampoline(t);
      this.gameLayer.addChild(node.view);
      Matter.Composite.add(this.engine.world, node.body);
      return node;
    });
    this.trampolineByBody.clear();
    for (const t of this.trampolines) this.trampolineByBody.set(t.body, t);

    this.boosters = (this.level.boosters ?? []).map((b) => {
      const node = new Booster(b);
      this.gameLayer.addChild(node.view);
      Matter.Composite.add(this.engine.world, node.body);
      return node;
    });
    this.boosterByBody.clear();
    for (const b of this.boosters) this.boosterByBody.set(b.body, b);

    this.winds = (this.level.wind ?? []).map((w) => {
      const node = new Wind(w);
      this.gameLayer.addChildAt(node.view, 0);
      Matter.Composite.add(this.engine.world, node.body);
      return node;
    });

    this.movingPlatforms = (this.level.movingPlatforms ?? []).map((mp) => {
      const node = new MovingPlatform(mp);
      // Path breadcrumbs render under the platform itself, above the bg.
      this.gameLayer.addChildAt(node.pathView, 0);
      this.gameLayer.addChild(node.view);
      Matter.Composite.add(this.engine.world, node.body);
      return node;
    });

    this.movingHazards = (this.level.movingHazards ?? []).map((mh) => {
      const node = new MovingHazard(mh);
      this.gameLayer.addChildAt(node.pathView, 0);
      this.gameLayer.addChild(node.view);
      Matter.Composite.add(this.engine.world, node.body);
      return node;
    });

    // Switches + gates form simple boolean circuits. Switches are sensor
    // pads that on overlap drive every gate sharing their `id`; gates are
    // platforms that toggle solid/sensor based on their circuit's state.
    this.switches = (this.level.switches ?? []).map((s) => {
      const node = new Switch(s);
      this.gameLayer.addChild(node.view);
      Matter.Composite.add(this.engine.world, node.body);
      return node;
    });
    this.switchByBody.clear();
    for (const s of this.switches) this.switchByBody.set(s.body, s);

    this.gates = (this.level.gates ?? []).map((g) => {
      const node = new Gate(g);
      this.gameLayer.addChild(node.view);
      Matter.Composite.add(this.engine.world, node.body);
      return node;
    });
    // Apply initial gate state so visuals match the inverted flag from the
    // first frame instead of waiting for the first switch poll.
    this.applyGateStates();

    // Checkpoints. If the player previously activated checkpoint N before
    // dying, mark all checkpoints up to N as armed so the visual matches the
    // current respawn state after the scene rebuild.
    this.checkpoints = (this.level.checkpoints ?? []).map((cp, i) => {
      const node = new Checkpoint(cp);
      this.gameLayer.addChild(node.view);
      Matter.Composite.add(this.engine.world, node.body);
      if (i <= this.activeCheckpointIdx) {
        node.trigger(0);
        node.triggeredAtSec = -10; // skip the punch animation on rebuild
      }
      return node;
    });

    this.finish = new Finish(this.level.finish);
    this.gameLayer.addChild(this.finish.view);
    Matter.Composite.add(this.engine.world, this.finish.body);

    // Checkpoint-overridden respawn: if the player armed checkpoint N before
    // dying, spawn there instead of the level's `spawn`.
    const respawnCp =
      this.activeCheckpointIdx >= 0 && this.activeCheckpointIdx < this.checkpoints.length
        ? this.checkpoints[this.activeCheckpointIdx]!
        : null;
    const spawnX = respawnCp ? respawnCp.x : this.level.spawn.x;
    const spawnY = respawnCp ? respawnCp.y - 30 : this.level.spawn.y; // pop out above the flag
    this.ragdoll = new Ragdoll(spawnX, spawnY);
    this.gameLayer.addChild(this.ragdoll.view);
    Matter.Composite.add(this.engine.world, this.ragdoll.composite);

    // Optional initial launch velocity so the player isn't a sitting duck
    // (Stickman Hook always shoots you sideways at level start).
    const sv = respawnCp
      ? { x: respawnCp.vx, y: respawnCp.vy }
      : (this.level.spawnVelocity ?? { x: 6, y: 0 });
    for (const body of this.ragdoll.allBodies()) {
      Matter.Body.setVelocity(body, sv);
    }

    // Reset the per-scene world clock that drives all path math.
    this.worldTimeSec = 0;

    this.hook = new Hook(
      this.engine,
      this.ragdoll,
      [
        ...this.platforms.map((p) => p.body),
        ...this.movingPlatforms.map((m) => m.body),
        ...this.gates.map((g) => g.body),
      ],
      {
        onAttach: () => { Sfx.hookAttach(); this.runStats.whips++; },
        onRelease: () => Sfx.hookRelease(),
        onMiss: () => Sfx.hookMiss(),
      },
    );
    this.gameLayer.addChild(this.hook.view);

    // Bind once per scene rebuild so we can safely .off() the previous engine's
    // listeners and avoid them piling up across level changes (the engine is
    // discarded each rebuild, but defensive cleanup keeps the pattern obvious).
    Matter.Events.on(this.engine, 'collisionStart', this.boundOnCollision);
    Matter.Events.on(this.engine, 'collisionActive', this.boundOnCollisionActive);

    this.camera = new Camera(this.worldContainer);
    this.camera.setViewport(this.app.renderer.screen.width, this.app.renderer.screen.height);
    this.camera.snapTo(spawnX, spawnY);

    this.hudText = new Text({
      text: `Level ${this.level.id} — ${this.level.name}`,
      style: { fill: 0xffffff, fontSize: 16, fontFamily: 'system-ui', fontWeight: '600' },
    });
    const ins = this.getSafeInsets();
    this.hudText.x = 16 + ins.left;
    this.hudText.y = 14 + ins.top;
    this.hudLayer.addChild(this.hudText);
    this.buildHud();
    this.fitHudTitle();

    this.overlay = new Container();
    this.overlay.visible = false;
    this.app.stage.addChild(this.overlay);
    // Wipe overlay-state caches; they get re-populated by showOverlay/
    // showWinOverlayWithReplayUI on the next render. Keep overlayOutcome
    // so the post-replay-loop microtask can re-show the win UI.
    this.overlayShowing = false;
    this.overlayHits = [];
    this.timelineFill = null;
    this.timelineRect = null;
    // The previous overlay's destroy({children:true}) just destroyed the
    // confetti's Graphics nodes; drop our stale reference so update()
    // doesn't try to mutate dead transforms (which throws and kills the
    // ticker, breaking subsequent tap routing).
    this.confetti = null;
    this.winContent = null;
    this.hamburgerBtn = null;
    this.hamburgerHit = null;

    // Keep the HUD (pause + fullscreen buttons) above the win/dead overlay
    // so the player can still tap them after dying or winning. Re-adding
    // moves the layer to the top of the stage's children array.
    this.app.stage.addChild(this.hudLayer);

    this.pauseOverlay = new Container();
    this.pauseOverlay.visible = false;
    // pauseOverlay sits above the HUD so the pause menu fully covers it.
    this.app.stage.addChild(this.pauseOverlay);
    this.pauseMenu = new PauseMenu(this.pauseOverlay);

    this.state = 'play';
    this.paused = false;
    this.accumulatorMs = 0;

    // Per-run replay bookkeeping. The seed/shim/_nextId were already set
    // up at the top of buildScene (must precede body construction — see
    // the comment there).
    this.frameCount = 0;
    this.recordedInputs = [];
    // Fresh stats for the new run. We don't clear `lastStats` here because
    // the death overlay reads from it after `state='dead'` is set; the
    // next live run will overwrite it on its own death/win.
    this.runStats = newRunStats();

    Sfx.start();

    // Touch hazards/platforms list to keep TS happy without unused-locals noise.
    void this.hazards;

    // When entering playback (e.g. opening a shared replay URL), surface
    // the hamburger immediately so the viewer can see they are watching a
    // replay and has an obvious affordance to exit — without waiting for
    // the first lap to complete.
    if (this.replayMode === 'playback') {
      this.addHamburgerButton();
      this.overlay.visible = true;
      const btn = this.hamburgerBtn as Container | null;
      if (btn !== null) {
        btn.alpha = 1;
        btn.visible = true;
      }
    }
  }

  private drawBackground(): void {
    const g = new Graphics();
    const minX = this.level.worldBounds.minX;
    const w = this.level.worldBounds.maxX - minX;
    const h = this.level.worldBounds.maxY;
    const { top, bottom } = resolveGradient(this.level);
    const tr = (top >> 16) & 0xff, tg = (top >> 8) & 0xff, tb = top & 0xff;
    const br = (bottom >> 16) & 0xff, bg = (bottom >> 8) & 0xff, bb = bottom & 0xff;
    // Many thin strips so banding disappears even at high zoom.
    const steps = Math.max(160, Math.min(512, Math.ceil(h / 3)));
    const stripH = h / steps;
    for (let i = 0; i < steps; i++) {
      // Smoothstep eases the transition so the middle band feels less linear.
      const tLin = i / (steps - 1);
      const t = tLin * tLin * (3 - 2 * tLin);
      // Tiny per-strip dither (\u00b11) breaks remaining quantization lines.
      const j = (i * 9301 + 49297) % 233280 / 233280 - 0.5;
      const r = Math.round(tr + (br - tr) * t + j * 1.5);
      const gc = Math.round(tg + (bg - tg) * t + j * 1.5);
      const b = Math.round(tb + (bb - tb) * t + j * 1.5);
      const color = (Math.max(0, Math.min(255, r)) << 16)
                  | (Math.max(0, Math.min(255, gc)) << 8)
                  |  Math.max(0, Math.min(255, b));
      // Overlap strips by 1px to hide aliasing seams.
      g.rect(minX, i * stripH - 0.5, w, stripH + 1).fill(color);
    }
    this.bgContainer.addChild(g);
  }

  // --------------------------------------------------------------------------

  private onCollision(evt: Matter.IEventCollision<Matter.Engine>): void {
    if (this.state !== 'play') return;
    const ragdollBodies = this.ragdoll.bodySet;
    for (const pair of evt.pairs) {
      const a = pair.bodyA, b = pair.bodyB;
      const aIsRag = ragdollBodies.has(a), bIsRag = ragdollBodies.has(b);
      if (!aIsRag && !bIsRag) continue;
      const ragBody = aIsRag ? a : b;
      const other = aIsRag ? b : a;
      if (other.label === 'hazard') { this.die(); return; }
      if (other.label === 'finish') { this.win(); return; }
      if (other.label === 'checkpoint') { this.touchCheckpoint(other); continue; }
      if (other.label === 'trampoline') {
        this.trampolineLaunch(other);
        continue;
      }
      if (other.label === 'booster') {
        this.boosterLaunch(other);
        continue;
      }
      if (other.label === 'platform') {
        // Funky physics: platforms never softlock. Reflect the contact normal
        // velocity component upward and ensure a minimum kick. This guarantees
        // the ragdoll always bounces off, even from rest.
        this.bouncePlatformContact(ragBody, pair);
      }
    }
  }

  /** Each tick: figure out which switches the ragdoll currently overlaps
   *  (sensor pads can't be observed via collisionStart reliably because
   *  Matter sometimes filters sensor-vs-sensor pairs), latch toggle state
   *  on rising edges, and propagate the resulting on/off state to every
   *  gate sharing each circuit `id`. Pure function of physics positions
   *  → deterministic across replays. */
  private updateSwitchesAndGates(): void {
    const ragdollBodies = this.ragdoll.bodyList;
    // 1. Recompute overlap state for every switch.
    for (const sw of this.switches) {
      const hit = Matter.Query.collides(sw.body, ragdollBodies).length > 0;
      const wasOverlapping = sw.overlapping;
      sw.overlapping = hit;
      if (sw.mode === 'toggle') {
        if (hit && !wasOverlapping && !sw.pressed) {
          // Toggle switches are one-shot: once activated they latch on and
          // stay on. This avoids fast on/off oscillation when the ragdoll's
          // multiple bodies (head/torso/limbs) graze the pad on a single
          // pass, each producing a fresh rising edge.
          sw.pressed = true;
          Sfx.switchOn();
        }
      } else {
        // Momentary: SFX on enter / leave so the player hears the gate
        // re-arm when they slide off the pad.
        if (hit && !wasOverlapping) Sfx.switchOn();
        else if (!hit && wasOverlapping) Sfx.switchOff();
      }
    }
    // 2. Per-circuit boolean: any switch whose effective output is "on".
    //    Toggle => latched `pressed`. Momentary => instantaneous overlap.
    const circuitOn: Map<string, boolean> = new Map();
    for (const sw of this.switches) {
      const on = sw.mode === 'momentary' ? sw.overlapping : sw.pressed;
      if (on) circuitOn.set(sw.id, true);
      else if (!circuitOn.has(sw.id)) circuitOn.set(sw.id, false);
    }
    // 3. Apply to gates. Default-closed unless inverted; circuit-on opens
    //    the gate (or closes it if inverted).
    for (const g of this.gates) {
      const on = circuitOn.get(g.id) ?? false;
      const closed = g.inverted ? on : !on;
      g.setClosed(closed);
    }
    // 4. Visuals (cheap fades). Run unconditionally so animations settle
    //    even when no overlap changed this frame.
    for (const sw of this.switches) sw.render();
    for (const g of this.gates) g.render();
  }

  /** Apply gate solidity matching the current switch states. Used at scene
   *  build so the inverted flag takes effect on frame 0. */
  private applyGateStates(): void {
    if (this.gates.length === 0) return;
    const circuitOn: Map<string, boolean> = new Map();
    for (const sw of this.switches) {
      const on = sw.mode === 'momentary' ? sw.overlapping : sw.pressed;
      if (on) circuitOn.set(sw.id, true);
    }
    for (const g of this.gates) {
      const on = circuitOn.get(g.id) ?? false;
      g.setClosed(g.inverted ? on : !on);
    }
  }

  /** Player's torso (or any limb) crossed a checkpoint sensor — arm it as
   *  the new respawn override and play a small confirmation. Cheap to call
   *  on every contact frame: `Checkpoint.trigger()` is idempotent. */
  private touchCheckpoint(cpBody: Matter.Body): void {
    for (let i = 0; i < this.checkpoints.length; i++) {
      const cp = this.checkpoints[i]!;
      if (cp.body !== cpBody) continue;
      if (cp.trigger(this.worldTimeSec) && i > this.activeCheckpointIdx) {
        this.activeCheckpointIdx = i;
        Sfx.hookAttach(); // re-use the bright "tink" sting; cheap & on-brand
      }
      break;
    }
  }

  private trampolineLaunch(tramp: Matter.Body): void {
    if (this.bouncedThisStep) return;
    this.bouncedThisStep = true;
    const node = this.trampolineByBody.get(tramp);
    const power = node?.power ?? 22;
    const dir = node?.direction ?? 'up';
    let dx = 0, dy = -1;
    if (dir === 'down')  { dx = 0; dy = 1; }
    if (dir === 'left')  { dx = -1; dy = 0; }
    if (dir === 'right') { dx = 1; dy = 0; }

    // Reactive boost: if the player is moving INTO the pad fast, return
    // some of that momentum on top of the base power. This makes a fall
    // onto a trampoline launch noticeably higher than a standstill tap.
    const tv = this.ragdoll.torso.velocity;
    const incoming = -(tv.x * dx + tv.y * dy); // positive = moving into pad
    const reactive = Math.max(0, incoming) * 0.31;
    const launchSpeed = power + reactive;

    // Tangential nudge so the player doesn't lock to a perfectly vertical
    // trajectory; preserves and slightly amplifies their sideways motion.
    const tangentMul = 1.04;
    const wobble = (this.gameRng() - 0.5) * 4;

    // Spin scaled by power so big pads send the ragdoll tumbling.
    const spinBase = (this.gameRng() - 0.5) * 0.36;
    const spin = spinBase * (1 + power / 50);

    for (const part of this.ragdoll.allBodies()) {
      const pv = part.velocity;
      let nvx: number, nvy: number;
      if (dx !== 0) {
        nvx = dx * launchSpeed;
        nvy = pv.y * tangentMul + wobble;
      } else {
        nvx = pv.x * tangentMul + wobble;
        nvy = dy * launchSpeed;
      }
      Matter.Body.setVelocity(part, { x: nvx, y: nvy });
      Matter.Body.setAngularVelocity(part, part.angularVelocity + spin);
    }

    // Visual + camera reaction. Intensity scales with how hard the hit was
    // (incoming velocity) plus base power tier so even a soft tap reads.
    const intensity = Math.min(1.5, 0.35 + reactive / 24 + power / 60);
    if (node) node.bounce(intensity * 0.48);
    this.camera.shake(1.2 + intensity * 2.8);

    Sfx.trampoline(launchSpeed);
  }

  private boosterLaunch(boost: Matter.Body): void {
    const node = this.boosterByBody.get(boost);
    if (!node) return;
    const spin = (this.gameRng() - 0.5) * 0.3;
    for (const part of this.ragdoll.allBodies()) {
      Matter.Body.setVelocity(part, { x: node.vx, y: node.vy });
      Matter.Body.setAngularVelocity(part, part.angularVelocity + spin);
    }
    Sfx.booster();
  }

  private bouncePlatformContact(body: Matter.Body, pair: Matter.Pair): void {
    if (this.bouncedThisStep) return;
    this.bouncedThisStep = true;
    void body;
    const MIN_KICK = 5; // px/step minimum outward speed after bounce
    const REFLECT = 0.55; // < 1 = energy loss on bounce
    const normal = pair.collision.normal;
    const fromAtoB = pair.bodyA === this.ragdoll.torso ? -1 :
      this.ragdoll.bodySet.has(pair.bodyA) ? -1 : 1;
    const nx = normal.x * fromAtoB;
    const ny = normal.y * fromAtoB;

    const v = this.ragdoll.torso.velocity;
    const vn = v.x * nx + v.y * ny;
    let outSpeed = vn < 0 ? -vn * REFLECT : vn;
    if (outSpeed < MIN_KICK) outSpeed = MIN_KICK;
    const dvx = (outSpeed - vn) * nx;
    const dvy = (outSpeed - vn) * ny;
    // Add a fun spin so the ragdoll tumbles after each bounce.
    const spin = (this.gameRng() - 0.5) * 0.15;
    for (const part of this.ragdoll.allBodies()) {
      const pv = part.velocity;
      Matter.Body.setVelocity(part, { x: pv.x + dvx, y: pv.y + dvy });
      Matter.Body.setAngularVelocity(part, part.angularVelocity + spin);
    }
    // Audible thud when the bounce was meaningful (skip near-rest contacts).
    if (-vn > 3 || outSpeed > 6) Sfx.thud();
  }

  /** Continuous-contact handler: catches the case where a body settled before
   * collisionStart's bounce was enough. Only fires a small kick when the body
   * is essentially at rest, so it doesn't keep injecting energy mid-bounce. */
  private onCollisionActive(evt: Matter.IEventCollision<Matter.Engine>): void {
    if (this.state !== 'play') return;
    const ragdollBodies = this.ragdoll.bodySet;
    const REST_SPEED2 = 0.5 * 0.5; // squared
    for (const pair of evt.pairs) {
      const a = pair.bodyA, b = pair.bodyB;
      const aIsRag = ragdollBodies.has(a), bIsRag = ragdollBodies.has(b);
      if (!aIsRag && !bIsRag) continue;
      const ragBody = aIsRag ? a : b;
      const other = aIsRag ? b : a;
      if (other.label !== 'platform') continue;
      const v = ragBody.velocity;
      if (v.x * v.x + v.y * v.y > REST_SPEED2) continue;
      this.bouncePlatformContact(ragBody, pair);
    }
  }

  private die(): void {
    if (this.state !== 'play') return;
    this.state = 'dead';
    this.hook.detach();
    this.ragdoll.mood = 'hurt';
    Sfx.death();
    this.camera.shake(18);
    // Replay-continuity: instead of rebuilding the scene on death (which
    // discards recordedInputs/frameCount/seed and breaks any chance of
    // playing back the pre-checkpoint segment), teleport-respawn in place
    // so the recording continues seamlessly across deaths.
    //
    // During playback we respawn immediately so the recorded post-death
    // frames replay deterministically without any overlay.
    if (this.replayMode === 'playback') {
      this.respawnInPlace();
      return;
    }
    this.overlayOutcome = 'dead';
    // Snapshot the run-so-far as a `result:'dead'` replay so the death
    // overlay's Share/Save/Copy/Load buttons have something to act on.
    // Replay length is the current frame count; inputs are the ones
    // recorded up to this exact tick. (Doesn't get persisted as a "best"
    // anywhere — Progress.ts only records wins.)
    this.lastReplay = {
      version: 'SHKR1',
      levelId: this.level.id,
      seed: this.currentSeed,
      frames: this.frameCount,
      result: 'dead',
      inputs: this.recordedInputs.slice(),
    };
    this.lastStats = { ...this.runStats };
    // Per-level death counter (live runs on builtin levels only).
    if (!this.testMode && this.levelIndex >= 0) {
      LevelStats.recordDeath(this.level.id, this.frameCount);
    }
    this.showDeathOverlayWithReplayUI();
  }

  private win(): void {
    if (this.state !== 'play') return;
    this.state = 'won';
    this.hook.detach();
    this.ragdoll.mood = 'happy';
    Sfx.win();
    if (this.replayMode === 'playback') {
      // For URL-shared sessions there's no live run, so promote the
      // pending replay to lastReplay the first time it completes — then
      // show the same replay UI a watched-replay would get (hamburger,
      // share/save/load, plus an exit-to-play button).
      if (!this.lastReplay && this.pendingReplay) {
        this.lastReplay = this.pendingReplay;
        this.overlayOutcome = 'won';
        this.winContentHiddenSticky = false;
        this.showWinOverlayWithReplayUI();
      }
      this.restartReplayLoop();
      return;
    }
    // Snapshot the run for the save buttons before overlay handlers can
    // mutate state. Coords already use world units that the input recorder
    // captured directly.
    this.lastReplay = {
      version: 'SHKR1',
      levelId: this.level.id,
      seed: this.currentSeed,
      frames: this.frameCount,
      result: 'won',
      inputs: this.recordedInputs.slice(),
    };
    this.lastStats = { ...this.runStats };
    // Persist personal best (and first-completion) so the level-select
    // modal can render the badge + offer to play back the saved run.
    // Skipped in test mode (editor playtests) and for user-built levels
    // — progress only tracks the shipped campaign levels.
    if (!this.testMode && this.levelIndex >= 0) {
      this.justSetNewBest = Progress.recordCompletion(this.lastReplay);
      LevelStats.recordWin(this.level.id, this.frameCount);
    } else {
      this.justSetNewBest = false;
    }
    this.overlayOutcome = 'won';
    this.winContentHiddenSticky = false;
    // Show the "photo finish": frozen world + confetti + time + a single
    // Replay button. Tap elsewhere advances to the next level; tap Replay
    // enters the full background-replay mode (with save/copy/load UI).
    this.showPhotoFinishOverlay();
  }

  private handleOverlayTap(): void {
    // During playback, any tap outside a button is a request to take
    // control. Watching a ghost loop while still seeing the win overlay
    // was confusing — players expected tap-to-play. The icon-button row
    // (Replay/Share/Save/...) is hit-tested earlier so explicit clicks
    // on those still work.
    if (this.replayMode === 'playback') {
      this.exitReplayAndPlay();
      return;
    }
    // Tapping continue clears any background-replay queue so the next scene
    // build starts a fresh recording.
    this.pendingReplay = null;
    this.overlayShowing = false;
    const outcome = this.overlayOutcome;
    this.overlayOutcome = null;
    if (outcome === 'won') {
      this.justSetNewBest = false;
      const isLast = this.testMode || this.levelIndex >= this.allLevels.length - 1;
      if (isLast) this.reloadCurrent();
      else this.nextLevel();
    } else if (outcome === 'dead') {
      // Soft retry: teleport back to the last checkpoint while preserving
      // the recording, world clock, and seed so the eventual replay covers
      // the entire run (including pre-checkpoint segments).
      this.respawnInPlace();
    }
  }

  private reloadCurrent(): void {
    if (this.testMode || this.levelIndex < 0) this.buildScene();
    else {
      // Restart on the same level: distinct from a generic abandon so the
      // stats can show "player retried this level N times" separately
      // from "player gave up and went elsewhere". Only meaningful while
      // mid-run; the dead/won states have their own counters.
      if (this.state === 'play') this.pendingAbandonKind = 'restart';
      this.loadLevelByIndex(this.levelIndex);
    }
  }
  private nextLevel(): void {
    if (this.testMode || this.levelIndex < 0) this.buildScene();
    else {
      // If the player taps Next while still mid-run (no win/death yet),
      // the abandon flush should record a "skip" rather than a generic
      // abandon to distinguish "I gave up on this one" from "I switched
      // to a different level via the picker".
      if (this.state === 'play') this.pendingAbandonKind = 'skip';
      this.loadLevelByIndex(this.levelIndex + 1);
    }
  }

  /** Teleport the ragdoll back to the active checkpoint (or level spawn) and
   *  resume play WITHOUT touching the world: physics objects keep ticking,
   *  worldTimeSec keeps advancing, recordedInputs keeps growing, currentSeed
   *  is unchanged. This is what makes a single replay span pre- and post-
   *  checkpoint death segments.
   *
   *  Used by the death-overlay tap and by playback (where the recorded death
   *  must reproduce the same teleport at the same frame). Explicit "Restart
   *  Level" / "R" key still go through reloadCurrent() for a hard reset. */
  private respawnInPlace(): void {
    const respawnCp =
      this.activeCheckpointIdx >= 0 && this.activeCheckpointIdx < this.checkpoints.length
        ? this.checkpoints[this.activeCheckpointIdx]!
        : null;
    const spawnX = respawnCp ? respawnCp.x : this.level.spawn.x;
    const spawnY = respawnCp ? respawnCp.y - 30 : this.level.spawn.y;
    const sv = respawnCp
      ? { x: respawnCp.vx, y: respawnCp.vy }
      : (this.level.spawnVelocity ?? { x: 6, y: 0 });

    this.hook.detach();
    // Translate every ragdoll body by the same delta so the figure lands at
    // the spawn point in roughly its current pose, then re-seed all
    // velocities so it launches consistently.
    const dx = spawnX - this.ragdoll.torso.position.x;
    const dy = spawnY - this.ragdoll.torso.position.y;
    for (const body of this.ragdoll.allBodies()) {
      Matter.Body.setPosition(body, { x: body.position.x + dx, y: body.position.y + dy });
      Matter.Body.setVelocity(body, sv);
      Matter.Body.setAngularVelocity(body, 0);
    }
    this.ragdoll.mood = 'happy';
    this.state = 'play';
    this.overlayShowing = false;
    this.overlayOutcome = null;
    this.overlay.removeChildren();
    this.overlayHits = [];
    this.camera.snapTo(spawnX, spawnY);
    // Reset all toggle switches to their unpressed state so the puzzle
    // restarts cleanly after a death. Otherwise switches the player had
    // flipped before dying stay latched, which both confuses level intent
    // and lets the player skip toggle puzzles by dying on purpose. Done
    // here (inside respawnInPlace) so the same reset happens during
    // deterministic replay playback at the matching frame.
    for (const sw of this.switches) {
      sw.pressed = false;
      sw.overlapping = false;
    }
    // Stats reflect the current attempt only — clear them on respawn so the
    // next death overlay shows numbers from this segment, not cumulative
    // across multiple tries.
    this.runStats = newRunStats();
  }

  // --- Pause / HUD ----------------------------------------------------------

  /** Read iOS / Android safe-area insets in CSS pixels. Returns 0s on
   *  platforms that don't expose env(safe-area-inset-*). Cheap to call
   *  every resize. */
  private getSafeInsets(): { top: number; right: number; bottom: number; left: number } {
    return this.safeArea.read();
  }

  private buildHud(): void {
    this.hud.build({
      width: this.app.renderer.screen.width,
      insets: this.getSafeInsets(),
      isFullscreen: !!document.fullscreenElement,
      onTogglePause: () => this.togglePause(),
      onToggleFullscreen: () => this.toggleFullscreen(),
    });
  }

  private toggleFullscreen(): void { toggleFullscreenImpl(); }

  private togglePause(): void {
    // Allow pausing even from win/dead overlays so the player can reach
    // pause-menu actions (jukebox, customize, restart, etc.) without first
    // having to dismiss the overlay.
    this.paused = !this.paused;
    Sfx.pause();
    if (this.paused) {
      this.pauseMenu.focusedIndex = 0;
      this.showPauseMenu();
    } else {
      this.pauseMenu.clearFocus();
      this.pauseOverlay.visible = false;
    }
  }

  private setMusicMuted(m: boolean): void {
    this.musicMuted = m;
    if (this.music) this.music.setMuted(m);
    try { localStorage.setItem('shk.musicMuted', m ? '1' : '0'); } catch { /* ignore */ }
    this.showPauseMenu(); // redraw to reflect new label
  }

  private setSfxMutedPersistent(m: boolean): void {
    setSfxMuted(m);
    try { localStorage.setItem('shk.sfxMuted', m ? '1' : '0'); } catch { /* ignore */ }
    this.showPauseMenu();
  }

  private showPauseMenu(): void {
    const isLast = this.levelIndex >= this.allLevels.length - 1;
    const inFs = !!document.fullscreenElement;
    const closePause = (): void => { this.paused = false; this.pauseOverlay.visible = false; };
    // Both Resume and Restart should hand control back to the player when
    // a replay is currently driving the scene — otherwise the pause menu
    // appears to do nothing because the ghost run keeps playing on top.
    // exitReplayAndPlay() already clears pendingReplay/replayMode and
    // re-builds the scene as a fresh live attempt.
    const inReplay = (): boolean => this.replayMode === 'playback';
    const items: PauseMenuItem[] = [
      { icon: 'play', label: 'Resume', tone: 'primary', action: () => {
        if (inReplay()) { closePause(); this.exitReplayAndPlay(); }
        else this.togglePause();
      } },
      { icon: 'restart', label: 'Restart', action: () => {
        closePause();
        if (inReplay()) this.exitReplayAndPlay();
        else this.reloadCurrent();
      } },
      { icon: 'next', label: isLast ? 'Replay' : 'Next', action: () => { closePause(); isLast ? this.reloadCurrent() : this.nextLevel(); } },
      { icon: 'levels', label: 'Levels', action: () => { closePause(); this.openLevelSelectModal(); } },
      // Jukebox is a side panel — it should NOT unpause the game. Just
      // hide the pause overlay so the modal isn't fighting it visually;
      // the modal restores the pause UI on close (see openJukeboxModal).
      { icon: 'jukebox', label: 'Jukebox', action: () => { this.pauseOverlay.visible = false; this.openJukeboxModal(); } },
      { icon: 'replay', label: 'Load Replay', action: () => { closePause(); this.openLoadReplayModal(); } },
      { icon: 'customize', label: 'Customize', action: () => { this.pauseOverlay.visible = false; this.openCustomizeModal(); } },
      { icon: 'stats', label: 'Level Stats', action: () => { this.pauseOverlay.visible = false; this.openLevelStatsModal(); } },
      { icon: 'input', label: 'Input', action: () => { this.pauseOverlay.visible = false; this.openInputModal(); } },
      this.musicMuted
        ? { icon: 'musicOff', label: 'Music off', tone: 'muted', action: () => this.setMusicMuted(false) }
        : { icon: 'musicOn',  label: 'Music on',  action: () => this.setMusicMuted(true) },
      isSfxMuted()
        ? { icon: 'sfxOff', label: 'SFX off', tone: 'muted', action: () => this.setSfxMutedPersistent(false) }
        : { icon: 'sfxOn',  label: 'SFX on',  action: () => this.setSfxMutedPersistent(true) },
      { icon: inFs ? 'fsOff' : 'fsOn', label: inFs ? 'Windowed' : 'Fullscreen', action: () => this.toggleFullscreen() },
    ];
    if (this.host.onEnterEditor && !this.testMode) {
      items.push({ icon: 'edit', label: 'Edit level', action: () => { closePause(); this.host.onEnterEditor!(this.level); } });
    }
    if (this.testMode && this.host.onExitTest) {
      items.push({ icon: 'back', label: 'To editor', action: () => { closePause(); this.host.onExitTest!(); } });
    } else if (!this.testMode) {
      items.push({ icon: 'home', label: 'Level 1', action: () => { closePause(); this.loadLevelByIndex(0); } });
    }
    if (!this.testMode && LevelStore.listUser().length > 0) {
      items.push({ icon: 'folder', label: `My Levels (${LevelStore.listUser().length})`, action: () => { closePause(); this.openMyLevelsModal(); } });
    }
    // Wrap each action in a Sfx click so the renderer stays UI-only.
    const wrapped = items.map((it) => ({ ...it, action: () => { Sfx.uiClick(); it.action(); } }));
    this.pauseMenu.show({
      width: this.app.renderer.screen.width,
      height: this.app.renderer.screen.height,
      insets: this.getSafeInsets(),
      title: 'Paused',
      subtitle: this.level.name,
      items: wrapped,
    });
  }

  /** HTML modal listing every level with name + index, clickable to jump.
   *  Includes the user's own levels at the bottom when present. */
  private openLevelSelectModal(): void {
    if (this.levelSelectModal) { this.levelSelectModal.close(); this.levelSelectModal = null; }
    this.levelSelectModal = openLevelSelectModalImpl({
      allLevels: this.allLevels,
      currentBuiltinIndex: this.testMode ? -1 : this.levelIndex,
      currentLevelName: this.level.name,
      onPickBuiltin: (i) => {
        // Picking a level from the picker means "play this" — drop any
        // in-flight replay queue so the same-level case gives control to
        // the player instead of silently re-entering playback.
        this.pendingReplay = null;
        this.loadLevelByIndex(i);
      },
      onPickUser: (entry) => {
        this.pendingReplay = null;
        this.testMode = false;
        try { this.loadLevelByData(entry.data); }
        catch (err) { console.warn('Failed to load user level', err); }
      },
      onWatchBest: (i, encoded) => {
        // Queue the saved best replay then load the level: same path the
        // Load Replay modal uses, so we get the full background-replay
        // visualisation (timeline, dim, ghost overlay, restart-on-finish).
        const r = Progress.loadBestReplay(this.allLevels[i]!.id);
        if (!r) { console.warn('Saved replay missing or corrupt', encoded.slice(0, 24)); return; }
        this.pendingReplay = r;
        this.loadLevelByIndex(i);
      },
      onClose: () => { this.levelSelectModal = null; },
    });
  }

  private showOverlay(title: string, subtitle: string): void {
    this.overlay.removeChildren();
    this.overlayHits = [];
    this.timelineFill = null;
    this.timelineRect = null;
    this.winContent = null;
    this.hamburgerBtn = null;
    this.hamburgerHit = null;
    this.winAutoHideAt = 0;
    this.winContentFading = false;
    this.winContentManuallyHidden = false;
    this.overlayShowing = true;
    const w = this.app.renderer.screen.width;
    const h = this.app.renderer.screen.height;

    // Everything text/buttons/timeline lives in winContent so we can fade it
    // out as a unit — INCLUDING the dim background, so the world becomes
    // fully visible once the player has had time to read the score.
    const content = new Container();
    this.overlay.addChild(content);
    this.winContent = content;

    const dim = new Graphics();
    dim.rect(0, 0, w, h).fill({ color: 0x000000, alpha: 0.45 });
    content.addChild(dim);

    // Confetti layer added LAST so pieces draw in the foreground above the
    // text. Sits outside winContent so the burst stays visible even after
    // the text fades out.
    if (this.confetti) { this.confetti.clear(); this.confetti.view.parent?.removeChild(this.confetti.view); }
    this.confetti = new Confetti();

    const titleText = new Text({
      text: title,
      style: { fill: 0xffffff, fontSize: 48, fontFamily: 'system-ui', fontWeight: '800' },
    });
    titleText.anchor.set(0.5);
    titleText.x = w / 2;
    titleText.y = h / 2 - 20;
    content.addChild(titleText);

    const sub = new Text({
      text: subtitle,
      style: { fill: 0xffffff, fontSize: 20, fontFamily: 'system-ui' },
    });
    sub.anchor.set(0.5);
    sub.x = w / 2;
    sub.y = h / 2 + 30;
    content.addChild(sub);

    // Confetti added LAST so its pieces draw above the title/subtitle/buttons.
    this.overlay.addChild(this.confetti.view);

    this.overlay.visible = true;
  }

  // --------------------------------------------------------------------------

  private update(deltaMs: number): void {
    // Poll gamepad before the paused check so Start can unpause.
    this.gamepadInput.poll();
    if (this.paused) return;
    if (this.state === 'play') {
      this.accumulatorMs += Math.min(deltaMs, FIXED_DT_MS * MAX_SUBSTEPS);
      let steps = 0;
      while (this.accumulatorMs >= FIXED_DT_MS && steps < MAX_SUBSTEPS) {
        this.bouncedThisStep = false;
        // Dispatch any replay inputs scheduled for this frame BEFORE physics
        // runs, so hook attach/detach affects the same step the recording did.
        if (this.replayMode === 'playback') this.dispatchReplayInputs();
        // Snapshot the pre-step transform so sync() can interpolate between
        // it and the post-step state — fixes 60 Hz physics strobing on
        // 120/144 Hz displays when the player is moving fast.
        this.ragdoll.snapshotPrev();
        // Advance moving things in lockstep with the physics tick. Path math
        // is purely a function of `worldTimeSec`, so replays reproduce the
        // exact same motion frame-for-frame.
        this.worldTimeSec += FIXED_DT_MS / 1000;
        const fixedDt = FIXED_DT_MS / 1000;
        for (const mp of this.movingPlatforms) mp.step(this.worldTimeSec, fixedDt);
        for (const mh of this.movingHazards) mh.step(this.worldTimeSec, fixedDt);
        for (const a of this.anchors) a.step(this.worldTimeSec);
        this.hook.step(fixedDt);
        this.applyAirAssist();
        this.applyZoneEffects();
        Matter.Engine.update(this.engine, FIXED_DT_MS);
        this.frameCount++;
        this.accumulatorMs -= FIXED_DT_MS;
        steps++;
        // Update fun-stats once per fixed step (after physics so velocities
        // reflect the just-resolved tick). Cheap: one sqrt + branches.
        this.tickRunStats(FIXED_DT_MS / 1000);
        // Auto-loop the background replay once it has played out, even if no
        // win/death event closed it (e.g. the player ragdolled into a hole).
        if (this.replayMode === 'playback' && this.pendingReplay
          && this.frameCount >= this.pendingReplay.frames + 30) {
          this.restartReplayLoop();
          return;
        }
      }
      if (this.ragdoll.position.y > this.level.worldBounds.maxY) {
        this.die();
      }
    }

    // Mood: hooked while a rope is attached; falling when in fast descent;
    // otherwise idle. Death/win states keep their mood (set in die()/win()).
    if (this.state === 'play') {
      if (this.hook.isAttached) this.ragdoll.mood = 'hooked';
      else if (this.ragdoll.torso.velocity.y > 8) this.ragdoll.mood = 'falling';
      else this.ragdoll.mood = 'idle';

      // Swoosh when moving fast through the air (rate-limited so it doesn't
      // dominate the audio bus). Only fires while not hooked, so it sells the
      // "flying" moments rather than overlapping with the rope tension.
      const v = this.ragdoll.torso.velocity;
      const speed2 = v.x * v.x + v.y * v.y;
      const now = performance.now();
      if (!this.hook.isAttached && speed2 > 14 * 14 && now >= this.nextSwooshAt) {
        const speed = Math.sqrt(speed2);
        Sfx.swoosh(Math.min(2, (speed - 14) / 6));
        this.nextSwooshAt = now + 380;
      }
    }

    // Render-time interpolation between the last two physics states. alpha
    // is the fraction of a fixed step still in the accumulator after the
    // catch-up loop above; passing it to ragdoll.sync removes the strobing
    // on high-refresh displays and is a no-op on a true 60 Hz screen.
    const alpha = this.accumulatorMs / FIXED_DT_MS;
    this.ragdoll.sync(alpha);
    this.hook.render();
    this.fitHudTitle();
    this.updateReplayTimeline();
    if (this.confetti) this.confetti.update(deltaMs / 1000);
    this.tickOverlayFade(deltaMs);

    // Show range rings only for anchors actually in reach. Track the
    // closest in-range anchor so it can pulse + glow brighter as a hint
    // to the player about which anchor would attach on tap.
    const px = this.ragdoll.position.x;
    const py = this.ragdoll.position.y;
    let nearestAnchor: typeof this.anchors[number] | null = null;
    let nearestD2 = Infinity;
    for (const a of this.anchors) {
      const dx = a.x - px, dy = a.y - py;
      const d2 = dx * dx + dy * dy;
      const inRange = d2 <= a.hookRadius * a.hookRadius;
      a.setInRange(inRange);
      if (inRange && d2 < nearestD2) {
        nearestAnchor = a;
        nearestD2 = d2;
      }
    }
    for (const a of this.anchors) a.setNearest(a === nearestAnchor);
    // Animate triggered checkpoints (scale punch + halo pulse).
    for (const cp of this.checkpoints) cp.render(this.worldTimeSec);

    // Switches + gates: refresh overlap state, propagate to circuits,
    // animate visuals.
    if (this.switches.length > 0 || this.gates.length > 0) {
      this.updateSwitchesAndGates();
    }

    const dtSec = Math.min(deltaMs, 50) / 1000;
    for (const t of this.trampolines) t.update(dtSec);
    // Follow the rendered (interpolated) torso so the camera tracks the
    // visible player rather than the stale physics body.
    const disp = this.ragdoll.displayPosition;
    this.camera.update(
      disp.x,
      disp.y,
      dtSec,
      this.level.worldBounds,
    );
  }

  /** Coalesce resize events: iOS fires several rapid `resize` /
   *  `orientationchange` / `visualViewport.resize` notifications during a
   *  single rotation, with the canvas backing-store size lagging behind.
   *  We schedule the actual recovery on rAF so we run after Pixi's own
   *  `resizeTo: window` listener has updated the renderer dimensions, and
   *  we re-run again on the next frame as belt-and-suspenders for the late
   *  iOS settle. */
  private scheduleResize(): void {
    if (this.resizePending) return;
    this.resizePending = true;
    const run = () => {
      this.resizePending = false;
      // First, force Pixi to re-read the current viewport in case its own
      // resizeTo listener missed an event (happens on iOS during rotation).
      try {
        const w = window.innerWidth;
        const h = window.innerHeight;
        if (this.app && this.app.renderer && (this.app.renderer.screen.width !== w || this.app.renderer.screen.height !== h)) {
          this.app.renderer.resize(w, h);
        }
      } catch { /* renderer may have been disposed */ }
      this.onResize();
      // Run once more next frame to absorb the late iOS settle (the second
      // `visualViewport.resize` often arrives ~1 frame after the first).
      requestAnimationFrame(() => {
        if (this.destroyed) return;
        this.onResize();
      });
    };
    requestAnimationFrame(run);
  }

  private onResize(): void {
    if (!this.camera) return;
    this.camera.setViewport(this.app.renderer.screen.width, this.app.renderer.screen.height);
    // Re-anchor to the player so the new viewport doesn't leave them
    // off-screen (e.g. when rotating from landscape to portrait the world
    // can be shorter than the screen and the spring would otherwise have to
    // crawl back to the player against the clamp).
    if (this.ragdoll) {
      this.camera.snapTo(this.ragdoll.position.x, this.ragdoll.position.y);
    }
    // HUD is laid out in screen-space; re-place pause button on the right.
    if (this.hudLayer && this.hudText) {
      const ins = this.getSafeInsets();
      this.hudText.x = 16 + ins.left;
      this.hudText.y = 14 + ins.top;
      this.hudLayer.removeChildren();
      this.hudLayer.addChild(this.hudText);
      this.buildHud();
      this.fitHudTitle();
    }
    if (this.paused) this.showPauseMenu();
    // If the win overlay was up, rebuild it so confetti/buttons/timeline
    // re-anchor to the new viewport size.
    if (this.overlayShowing && this.overlayOutcome === 'won' && this.lastReplay) {
      if (this.replayMode === 'playback') this.showWinOverlayWithReplayUI();
      else this.showPhotoFinishOverlay();
    } else if (this.overlayShowing && this.overlayOutcome === 'dead') {
      this.showOverlay('Ouch!', 'Tap to retry');
    }
  }

  /** Auto-scale the HUD title so it never overlaps the right-side button cluster. */
  private fitHudTitle(): void {
    if (!this.hudText) return;
    this.hudText.scale.set(1);
    // Use renderer.screen (CSS pixels) — renderer.width is the backing
    // framebuffer (CSS × resolution), which over-reports available space on
    // retina and prevents the title from shrinking when it should.
    const screenW = this.app.renderer.screen.width;
    const available = Math.max(60, screenW - 16 - 100);
    const w = this.hudText.width;
    if (w > available) this.hudText.scale.set(available / w);
  }

  // --- ESP32 hardware button ------------------------------------------------

  /** Called by EspButtonClient when a device reports a button press.
   *  Mirrors the Space-key down handler exactly so replay determinism and
   *  all existing guards remain intact. */
  private handleEspDown(_deviceId: string): void {
    if (this.paused) return;
    if (this.state !== 'play') { this.handleOverlayTap(); return; }
    if (this.replayMode === 'playback') return;
    const p = this.ragdoll.position;
    this.recordedInputs.push({ frame: this.frameCount, kind: 'D', x: p.x, y: p.y });
    this.hook.tryAttach(this.anchors);
  }

  /** Called by EspButtonClient when a device reports a button release. */
  private handleEspUp(_deviceId: string): void {
    if (this.paused) return;
    if (this.replayMode === 'playback') return;
    if (this.state !== 'play') return;
    this.recordedInputs.push({ frame: this.frameCount, kind: 'U' });
    this.hook.detach();
  }

  /** Build and connect an EspButtonClient for each URL. */
  private buildEspClients(urls: string[]): EspButtonClient[] {
    return urls.map((url) => {
      const c = new EspButtonClient(url, {
        onDown: (id) => this.handleEspDown(id),
        onUp:   (id) => this.handleEspUp(id),
        onPresence: (id, online) => console.debug(`[esp] ${id} ${online ? 'online' : 'offline'}`),
      });
      c.connect();
      return c;
    });
  }

  /** Tear down existing ESP clients and recreate from the given URL list.
   *  Called when the Input modal saves new URLs. */
  private reconnectEspClients(urls: string[]): void {
    for (const c of this.espClients) c.close();
    this.espClients = this.buildEspClients(urls);
  }

  /** Read configured ESP32 WebSocket URLs from localStorage.
   *  Returns an empty array when nothing is configured (zero overhead). */
  private readEspButtonUrls(): string[] {
    try {
      const raw = JSON.parse(localStorage.getItem('shk.espButtons.v1') ?? '[]') as unknown;
      if (!Array.isArray(raw)) return [];
      return raw.filter((u): u is string => typeof u === 'string' && u.startsWith('ws'));
    } catch { return []; }
  }

  // --------------------------------------------------------------------------

  /** Comeback assist: when not hooked and the torso has lost most forward
   *  momentum, give a tiny continuous nudge in the level's progress direction.
   *  Keeps the game moving so a flubbed swing doesn't end the run. */
  private applyAirAssist(): void {
    if (this.hook.isAttached) return;
    const torso = this.ragdoll.torso;
    if (torso.velocity.x > 1.5) return; // already moving forward
    const NUDGE = 0.00015; // very gentle horizontal force
    for (const part of this.ragdoll.allBodies()) {
      Matter.Body.applyForce(part, part.position, { x: NUDGE * part.mass, y: 0 });
    }
  }

  /** Per-substep environmental forces: wind zones add a steady push.
   *  Cheap AABB tests. */
  private applyZoneEffects(): void {
    let nowInWind = false;
    const tx = this.ragdoll.torso.position.x, ty = this.ragdoll.torso.position.y;
    for (const part of this.ragdoll.allBodies()) {
      const px = part.position.x, py = part.position.y;
      for (const w of this.winds) {
        const b = w.bounds;
        if (px < b.minX || px > b.maxX || py < b.minY || py > b.maxY) continue;
        Matter.Body.applyForce(part, part.position, {
          x: w.fx * part.mass,
          y: w.fy * part.mass,
        });
      }
    }
    for (const w of this.winds) {
      const b = w.bounds;
      if (tx >= b.minX && tx <= b.maxX && ty >= b.minY && ty <= b.maxY) { nowInWind = true; break; }
    }
    if (nowInWind && !this.inWind) Sfx.swoosh(1.2);
    this.inWind = nowInWind;
  }

  // --- Replay ---------------------------------------------------------------

  /** Redraw the timeline progress fill if a replay is currently playing
   *  back AND the win overlay's timeline rect is mounted. Cheap: a single
   *  rect.clear/fill per frame. */
  private updateReplayTimeline(): void {
    const fill = this.timelineFill;
    const rect = this.timelineRect;
    if (!fill || !rect) return;
    let progress = 0;
    if (this.replayMode === 'playback' && this.pendingReplay) {
      progress = Math.min(1, this.frameCount / Math.max(1, this.pendingReplay.frames));
    } else if (this.lastReplay) {
      // After the live run finishes but before playback starts, show full bar.
      progress = 1;
    }
    fill.clear();
    if (progress > 0) {
      fill.roundRect(rect.x, rect.y, rect.w * progress, rect.h, rect.h / 2)
        .fill({ color: 0xffffff, alpha: 0.85 });
    }
  }

  /** Fire any recorded inputs whose frame matches the upcoming substep.
   *  Called at the start of each substep while in playback mode. */
  private dispatchReplayInputs(): void {
    const r = this.pendingReplay;
    if (!r) return;
    while (this.replayCursor < r.inputs.length
        && r.inputs[this.replayCursor]!.frame <= this.frameCount) {
      const ev = r.inputs[this.replayCursor]!;
      this.replayCursor++;
      if (ev.kind === 'D') this.hook.tryAttach(this.anchors);
      else this.hook.detach();
    }
  }

  /** Re-loop the currently-watched playback. Falls back to entering
   *  replay mode for the first time when called before any playback was
   *  active (e.g. the death overlay's Watch-replay button — `pendingReplay`
   *  is null in the live-death state because death never set it). */
  private restartReplayLoop(): void {
    if (!this.pendingReplay) { this.enterReplayMode(); return; }
    this.scheduleReplayRebuild();
  }

  /** Single source of truth for transitions into / between playback
   *  iterations. Defers to the next tick so we don't rebuild mid-substep,
   *  then re-mounts the watch UI matching the recorded outcome so the
   *  hamburger + share/save row are always present whether the recording
   *  ended in a win or a death. */
  private scheduleReplayRebuild(): void {
    queueMicrotask(() => {
      if (this.destroyed) return;
      this.buildScene();
      this.showReplayWatchUI();
    });
  }

  /** Pick the win-replay or death-replay overlay based on the active
   *  playback's recorded outcome. Both variants now install the hamburger
   *  so the viewer can collapse / re-show the UI uniformly. */
  private showReplayWatchUI(): void {
    if (this.overlayOutcome === 'dead') this.showDeathOverlayWithReplayUI();
    else this.showWinOverlayWithReplayUI();
  }

  /** Exit replay playback and start a fresh live attempt at the same level.
   *  Wired to the `play` button on the replay overlay so a viewer of a
   *  shared replay (or someone watching a saved demo) can jump in and play. */
  private exitReplayAndPlay(): void {
    this.pendingReplay = null;
    this.replayMode = null;
    this.lastReplay = null;
    this.overlayOutcome = null;
    this.overlayShowing = false;
    this.winContentHiddenSticky = false;
    this.winContentManuallyHidden = false;
    this.winAutoHideAt = 0;
    this.justSetNewBest = false;
    this.reloadCurrent();
  }

  /** Promote the last recorded run to the active playback source and
   *  schedule the deterministic re-run with the matching watch UI. Used
   *  by both the photo-finish Watch button and the death-overlay Watch
   *  button — the only difference is which overlay `showReplayWatchUI`
   *  picks (driven by `overlayOutcome` set in win()/die()). */
  private enterReplayMode(): void {
    if (!this.lastReplay) return;
    this.pendingReplay = this.lastReplay;
    this.scheduleReplayRebuild();
  }

  private showWinOverlayWithReplayUI(): void {
    if (!this.lastReplay) return;
    const isLast = this.testMode || this.levelIndex >= this.allLevels.length - 1;
    const timeStr = formatFrameTime(this.lastReplay.frames);
    this.overlayOutcome = 'won';
    // No 'Level cleared!' title here — the photo-finish overlay shown
    // immediately after the win already announced it. This view is the
    // ongoing replay UI, so just show the time + the next-action hint.
    const statsLine = this.lastStats ? `\n${formatStatsLine(this.lastStats)}` : '';
    this.showOverlay('', `${timeStr}  \u2014  ${isLast ? 'Tap to replay' : 'Tap for next level'}${statsLine}`);
    this.addReplayButtonsToWinOverlay();
    // Preserve the player's hide-preference across replay-loop rebuilds:
    // if the UI was hidden before the rebuild, immediately hide the
    // freshly rebuilt UI too.
    if (this.winContentHiddenSticky && this.winContent) {
      this.winContent.visible = false;
      this.winContent.alpha = 0;
      this.winContentManuallyHidden = true;
      this.winAutoHideAt = 0;
      if (this.hamburgerBtn) { this.hamburgerBtn.visible = true; this.hamburgerBtn.alpha = 1; }
    }
  }

  /** Update per-run "fun stats" (top speed, air time). Called once per
   *  fixed physics substep from `update()`. Cheap O(1) work; safe to run
   *  during playback (we'll just overwrite `runStats` on the next live
   *  buildScene anyway). */
  private tickRunStats(dtSec: number): void {
    const v = this.ragdoll.torso.velocity;
    const speed = Math.hypot(v.x, v.y) * 60; // matter velocity is per-step; *60 -> per-second
    if (speed > this.runStats.topSpeed) this.runStats.topSpeed = speed;
    // "In the air" = no ground/platform contact AND not currently roped to
    // an anchor. The hook attach state is the cheapest proxy for "swinging"
    // which we count as not-on-ground for the purposes of this metric.
    if (!this.hook.isAttached) {
      // Approximate ground contact via vertical velocity near zero AND
      // torso below the screen-bottom guard. Cheaper than scanning pairs:
      // any non-trivial |vy| means we're in flight or just bounced.
      if (Math.abs(v.y) > 0.5) this.runStats.airTimeS += dtSec;
    }
  }

  /** Mirror of `showWinOverlayWithReplayUI` for the death ("Ouch!") path.
   *  Surfaces the same Replay/Share/Save/Copy/Load buttons so a player can
   *  share or save a run that ended in death without first winning the
   *  level. Tap-elsewhere still falls through to `handleOverlayTap` which
   *  performs the soft-respawn at the last checkpoint. No auto-hide here:
   *  the player needs to read the message before deciding to retry. */
  private showDeathOverlayWithReplayUI(): void {
    const statsLine = this.lastStats ? `\n${formatStatsLine(this.lastStats)}` : '';
    const tapHint = this.replayMode === 'playback' ? 'Tap to play' : 'Tap to retry';
    this.showOverlay('Ouch!', `${tapHint}${statsLine}`);
    this.installReplayButtonRow([
      { icon: 'replay', title: 'Watch replay',     action: () => this.enterReplayMode() },
      { icon: 'share',  title: 'Copy share link',  action: () => { void this.shareLastReplay(); } },
      { icon: 'save',   title: 'Save replay',      action: () => this.downloadLastReplay() },
      { icon: 'copy',   title: 'Copy replay text', action: () => this.copyLastReplayToClipboard() },
      { icon: 'load',   title: 'Load replay\u2026', action: () => this.openLoadReplayModal() },
    ]);
    // Symmetric with the win-replay overlay: install the hamburger so the
    // viewer can collapse the UI and watch the looped death replay
    // unobstructed. No auto-hide here — the player needs to read 'Ouch!'
    // before deciding whether to retry or watch.
    if (this.lastReplay && this.winContent) this.addHamburgerButton();
  }

  /** Shared layout for the icon-button row used on both the win and death
   *  replay overlays. Wraps every action in a Sfx click and wires the
   *  resulting hit rects + timeline graphics back into the scene. No-ops
   *  if there's no replay to act on or the overlay content isn't built. */
  private installReplayButtonRow(buttons: ReplayButtonSpec[]): void {
    if (!this.lastReplay) return;
    const content = this.winContent;
    if (!content) return;
    const w = this.app.renderer.screen.width;
    const h = this.app.renderer.screen.height;
    const wrapped = buttons.map((b) => ({ ...b, action: () => { Sfx.uiClick(); b.action(); } }));
    const row = buildReplayButtonRow({ content, width: w, height: h, buttons: wrapped });
    this.overlayHits.push(...row.hits);
    this.timelineFill = row.timelineFill;
    this.timelineRect = row.timelineRect;
  }

  /** "Photo finish": frozen world overlay shown immediately after a win,
   *  before any replay loop kicks in. Shows the time, a confetti burst, and
   *  a single Replay icon button. Tapping the button enters background
   *  replay mode (full save/copy/load UI). Tapping anywhere else advances
   *  to the next level (handled by handleOverlayTap via overlayOutcome). */
  private showPhotoFinishOverlay(): void {
    if (!this.lastReplay) return;
    const isLast = this.testMode || this.levelIndex >= this.allLevels.length - 1;
    const timeStr = formatFrameTime(this.lastReplay.frames);
    const statsLine = this.lastStats ? `\n${formatStatsLine(this.lastStats)}` : '';
    this.showOverlay(
      'Level cleared!',
      `${timeStr}  \u2014  ${isLast ? 'Tap to replay' : 'Tap to continue'}${statsLine}`,
    );
    const content = this.winContent;
    if (!content) return;
    // Confetti burst right now; the world is frozen (state='won') so we
    // don't need to defer past any rebuild this time.
    if (this.confetti) {
      const w = this.app.renderer.screen.width;
      const h = this.app.renderer.screen.height;
      this.confetti.burst(w * 0.3, h * 0.55, 90);
      this.confetti.burst(w * 0.7, h * 0.55, 90);
      // Personal-best celebration: extra burst from the top of the screen
      // so it visually rains down behind the "NEW BEST!" subtitle.
      if (this.justSetNewBest) {
        this.confetti.burst(w * 0.5, h * 0.15, 140);
      }
    }
    if (this.justSetNewBest) {
      const banner = new Text({
        text: 'NEW BEST!',
        style: {
          fill: 0xffe066, fontSize: 28, fontFamily: 'system-ui',
          fontWeight: '900', letterSpacing: 3,
          stroke: { color: 0x1b0f30, width: 4 },
        },
      });
      banner.anchor.set(0.5);
      banner.x = this.app.renderer.screen.width / 2;
      // Sit above the "Level cleared!" title (which is at h/2 - 20 with
      // 48px text, so its top edge is around h/2 - 44).
      banner.y = this.app.renderer.screen.height / 2 - 90;
      content.addChild(banner);

      // Purposeful happiness animation: a springy "boing" entry that
      // scales up from 0, overshoots, then settles into a perpetual
      // gentle bounce + slow side-to-side wiggle. Pure cosmetic — runs
      // off the Pixi ticker and self-removes when the banner is gone.
      const start = performance.now();
      const tick = () => {
        if (banner.destroyed || !banner.parent) {
          this.app.ticker.remove(tick);
          return;
        }
        const tSec = (performance.now() - start) / 1000;
        // Entry: critically-damped-ish overshoot that settles by ~0.6s.
        const entry = tSec < 0.6
          ? 1 - Math.cos(Math.min(tSec / 0.6, 1) * Math.PI * 1.5) * Math.exp(-tSec * 4)
          : 1;
        // Idle: small breathing scale + slight rotational wiggle.
        const breathe = 1 + Math.sin(tSec * 6) * 0.06;
        const wiggle = Math.sin(tSec * 3.2) * 0.08; // ~4.5° peak
        banner.scale.set(entry * breathe);
        banner.rotation = wiggle;
      };
      this.app.ticker.add(tick);
    }
    // Single Replay icon button. Hit-test routes to enterReplayMode; tap
    // outside it falls through to handleOverlayTap (next level / restart).
    const w = this.app.renderer.screen.width;
    const h = this.app.renderer.screen.height;
    const SIZE = 44;
    // Wide enough that the labels ("Watch replay" / "Share replay" —
    // ~80–85px each) never collide on the narrowest mobile viewport.
    const GAP = 100;
    // Two side-by-side options: Watch replay + Share replay.
    const totalW = SIZE * 2 + GAP;
    const startX = (w - totalW) / 2;
    const y = h / 2 + 80;
    const watchBtn = this.makeIconButton('replay', startX, y, SIZE, () => {
      Sfx.uiClick();
      this.enterReplayMode();
    });
    content.addChild(watchBtn.node);
    this.overlayHits.push(watchBtn.hit);
    const watchLabel = new Text({
      text: 'Watch replay',
      style: { fill: 0xffffff, fontSize: 13, fontFamily: 'system-ui', fontStyle: 'italic' },
    });
    watchLabel.anchor.set(0.5);
    watchLabel.x = startX + SIZE / 2;
    watchLabel.y = y + SIZE + 14;
    content.addChild(watchLabel);

    const shareBtn = this.makeIconButton('share', startX + SIZE + GAP, y, SIZE, () => {
      Sfx.uiClick();
      void this.shareLastReplay();
    });
    content.addChild(shareBtn.node);
    this.overlayHits.push(shareBtn.hit);
    const shareLabel = new Text({
      text: 'Share replay',
      style: { fill: 0xffffff, fontSize: 13, fontFamily: 'system-ui', fontStyle: 'italic' },
    });
    shareLabel.anchor.set(0.5);
    shareLabel.x = startX + SIZE + GAP + SIZE / 2;
    shareLabel.y = y + SIZE + 14;
    content.addChild(shareLabel);
  }

  /** Render compact icon-only buttons (matching the editor toolbar style)
   *  under the win overlay text and register their hit rects. All UI is
   *  placed in `winContent` so it can be faded as a unit. */
  private addReplayButtonsToWinOverlay(): void {
    this.installReplayButtonRow([
      { icon: 'play',   title: 'Play this level',   action: () => this.exitReplayAndPlay() },
      { icon: 'replay', title: 'Replay',            action: () => this.restartReplayLoop() },
      { icon: 'share',  title: 'Copy share link',   action: () => { void this.shareLastReplay(); } },
      { icon: 'save',   title: 'Save replay',       action: () => this.downloadLastReplay() },
      { icon: 'copy',   title: 'Copy replay text',  action: () => this.copyLastReplayToClipboard() },
      { icon: 'load',   title: 'Load replay\u2026', action: () => this.openLoadReplayModal() },
    ]);
    if (!this.lastReplay || !this.winContent) return;

    // Always have the hamburger present so once the UI fades the player can
    // bring it back. Drawn on the overlay (not inside content) so it
    // remains visible while content is hidden/faded.
    this.addHamburgerButton();

    // Auto-hide timer: 4.5s after the win UI appears, fade the content out
    // so the player can enjoy watching the replay loop unobstructed.
    this.winAutoHideAt = performance.now() + 4500;
  }

  /** Thin wrapper around the shared ReplayHud helper so existing call sites
   *  (the photo-finish Watch/Share row) keep their signature. */
  private makeIconButton(
    icon: 'replay' | 'save' | 'copy' | 'load' | 'menu' | 'share' | 'play',
    x: number, y: number, size: number, action: () => void,
  ): { node: Container; hit: { x: number; y: number; w: number; h: number; action: () => void } } {
    return buildIconButton(icon, x, y, size, action);
  }

  /** Floating hamburger button anchored under the HUD pause/fullscreen
   *  cluster. Hidden while winContent is visible. */
  private addHamburgerButton(): void {
    const { node, hit } = makeHamburgerButton({
      overlay: this.overlay,
      width: this.app.renderer.screen.width,
      action: () => { Sfx.uiClick(); this.toggleWinContent(); },
    });
    this.hamburgerBtn = node;
    this.hamburgerHit = hit;
  }

  /** Toggle the win-overlay content (title/subtitle/buttons/timeline)
   *  visibility, with fade-in/out handled by direct alpha for simplicity. */
  private toggleWinContent(): void {
    if (!this.winContent) return;
    const showing = this.winContent.visible && this.winContent.alpha > 0.1;
    if (showing) {
      this.winContent.visible = false;
      this.winContent.alpha = 0;
      this.winContentManuallyHidden = true;
      this.winContentFading = false;
      this.winContentHiddenSticky = true;
      this.winAutoHideAt = 0;
      if (this.hamburgerBtn) { this.hamburgerBtn.visible = true; this.hamburgerBtn.alpha = 1; }
    } else {
      this.winContent.visible = true;
      this.winContent.alpha = 1;
      this.winContentManuallyHidden = true; // suppress auto-hide after manual show
      this.winContentFading = false;
      this.winContentHiddenSticky = false;
      this.winAutoHideAt = 0;
      if (this.hamburgerBtn) { this.hamburgerBtn.visible = false; this.hamburgerBtn.alpha = 0; }
    }
  }

  /** Per-frame: handle the auto-hide timer + fade animation. */
  private tickOverlayFade(deltaMs: number): void {
    if (!this.overlayShowing || !this.winContent) return;
    if (this.winContentManuallyHidden) return;
    // Trigger the fade once the auto-hide timer elapses.
    if (this.winAutoHideAt > 0 && performance.now() >= this.winAutoHideAt) {
      this.winAutoHideAt = 0;
      this.winContentFading = true;
    }
    if (!this.winContentFading) return;
    const fadeStep = deltaMs / 500; // 500ms fade
    this.winContent.alpha = Math.max(0, this.winContent.alpha - fadeStep);
    if (this.winContent.alpha <= 0) {
      this.winContent.visible = false;
      this.winContentFading = false;
      this.winContentHiddenSticky = true;
    }
    if (this.hamburgerBtn) {
      this.hamburgerBtn.visible = true;
      this.hamburgerBtn.alpha = Math.min(1, this.hamburgerBtn.alpha + fadeStep);
    }
  }

  private downloadLastReplay(): void {
    if (!this.lastReplay) return;
    const text = encodeReplay(this.lastReplay);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stickhook_${this.lastReplay.levelId}_${this.lastReplay.frames}f.shkr.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private async copyLastReplayToClipboard(): Promise<void> {
    if (!this.lastReplay) return;
    const text = encodeReplay(this.lastReplay);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback: open the load modal pre-filled so the user can copy by hand.
      this.openLoadReplayModal(text);
    }
  }

  /** Build a self-contained share URL (replay + level inlined for user
   *  levels) and copy it to the clipboard. Falls back to the load modal
   *  prefilled with the URL when the clipboard API is blocked (e.g. iOS
   *  Safari without a user-gesture transient activation). */
  private async shareLastReplay(): Promise<void> {
    if (!this.lastReplay) return;
    const { buildShareUrl } = await import('../gameplay/ShareLink.js');
    const { getSongs, pickSongForLevel } = await import('../audio/Music.js');
    // For built-in levels: omit the inline level; the receiver's catalog
    // resolves it by id. For user levels (or test mode): inline the JSON.
    const isBuiltin = this.allLevels.some((l) => l.id === this.lastReplay!.levelId);
    // Track override: only include in the URL when it differs from what
    // the receiver would otherwise hear (the level's default pick). No
    // override → omit entirely so the link stays short.
    const playing = this.music?.getSong();
    const defaultName = pickSongForLevel(this.lastReplay.levelId).name;
    const trackParam: { track?: Song } = (playing && playing.name !== defaultName)
      ? { track: playing }
      : {};
    const baseUrl = window.location.origin + window.location.pathname;
    const url = await buildShareUrl({
      baseUrl,
      replay: this.lastReplay,
      builtinTrackNames: getSongs().map((s) => s.name),
      ...(isBuiltin ? {} : { level: this.level }),
      ...trackParam,
    });
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      this.openLoadReplayModal(url);
    }
  }

  /** DOM modal: paste a demo, hit Load. Dual-purpose: also used as a fallback
   *  when the clipboard API is unavailable, in which case `prefill` shows the
   *  current run for the user to copy manually. */
  private openLoadReplayModal(prefill = ''): void {
    if (this.replayModal) { this.replayModal.close(); this.replayModal = null; }
    this.replayModal = openLoadReplayModalImpl({
      prefill,
      allLevels: this.allLevels,
      onLoad: (r, target) => {
        this.pendingReplay = r;
        if (target.kind === 'builtin') {
          this.loadLevelByIndex(target.index);
        } else {
          this.testMode = false;
          this.loadLevelByData(target.data);
        }
      },
      onLoadShare: (payload) => {
        // Share URL pasted into the modal: treat it the same as the
        // boot-from-URL path. If the link carries an inline level we
        // load that directly so the replay stays deterministic even
        // when the receiver doesn't have the source level installed.
        this.pendingReplay = payload.replay;
        if (payload.level) {
          this.testMode = false;
          this.loadLevelByData(payload.level);
        } else {
          const idx = this.allLevels.findIndex((l) => l.id === payload.replay.levelId);
          this.loadLevelByIndex(idx >= 0 ? idx : 0);
        }
      },
      onClose: () => { this.replayModal = null; },
    });
  }
}
